import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildKotPrintHtml as buildKotPrintHtmlShared, printKotById, buildBillPrintHtml, openBillPrintWindow } from '../utils/kotPrint';
import { fmtTime } from '../utils/dateUtils';
import { isRoomServiceEligible } from '../utils/roomServiceEligibility';
import ServiceTypeSelector from '../components/orders/ServiceTypeSelector';
import TableSelector from '../components/orders/TableSelector';
import RoomSelector from '../components/orders/RoomSelector';
import MenuAndOrder from '../components/orders/MenuAndOrder';

const ORDER_TYPE_ICONS = { ROOM_SERVICE: '🛏️', DINE_IN: '🍴', TAKE_AWAY: '🥡', DELIVERY: '🛵' };
const ORDER_STATUS_COLORS = { PROCESSING: '#b45309', BILLED: '#1d4ed8', SETTLED: '#15803d', PENDING: '#dc2626', CANCELLED: '#475569' };

const ORDER_TAB_META = {
  ALL: { title: 'All Orders', subtitle: 'Every order across every channel, in one place' },
  ROOM_SERVICE: { title: 'Room Service Orders', subtitle: 'Delivering great food experience to your guests' },
  DINE_IN: { title: 'Dine-In Orders', subtitle: 'Freshly prepared for a delightful dining experience' },
  TAKE_AWAY: { title: 'Take Away Orders', subtitle: 'Packed up and ready for pickup' },
  DELIVERY: { title: 'Delivery Orders', subtitle: 'On the way to happy customers' },
};

function KOTModule({ token, selectedProperty, selectedOutletId = '', selectedOutlet, initialBillId, onInitialBillConsumed }) {
  const detectMobileUi = () => {
    if (typeof window === 'undefined') return false;
    const coarsePointer = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    const uaMobile = /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(window.navigator?.userAgent || '');
    return (coarsePointer || uaMobile) && window.innerWidth <= 1024;
  };
  const [isMobile, setIsMobile] = useState(() => detectMobileUi());
  const [scope, setScope] = useState({
    chain_id: selectedProperty?.chain_id || '',
    property_id: selectedProperty?.id || '',
    outlet_id: selectedOutletId || ''
  });

  const [tables, setTables] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [stations, setStations] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [taxProfiles, setTaxProfiles] = useState([]);
  const [pricingProfiles, setPricingProfiles] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [kots, setKots] = useState([]);
  const serverTimeOffsetRef = useRef(0); // ms: server epoch minus client epoch, computed from HTTP Date header
  const [refreshing, setRefreshing] = useState(false);
  const [billPreview, setBillPreview] = useState(null);
  const [kotPreview, setKotPreview] = useState(null);
  // Generate-bill preview (shown BEFORE the bill is generated). Lets the
  // user review items and apply a bill-level discount (% or fixed) before
  // committing. `{ orderId, items, discountType, discountValue, generating }`.
  const [generatePreview, setGeneratePreview] = useState(null);
  // Table-bill preview: consolidated bill for multiple orders on a table.
  // `{ table, orderIds, items, discountType, discountValue, generating }`
  const [tableBillPreview, setTableBillPreview] = useState(null);
  const [settlePanel, setSettlePanel] = useState(false);
  const [settleMode, setSettleMode] = useState('CASH');
  const [settleAmount, setSettleAmount] = useState('');
  const [settleRef, setSettleRef] = useState('');
  const [settleRoomNumber, setSettleRoomNumber] = useState('');
  const [settleGuestName, setSettleGuestName] = useState('');
  const [settleMobileLast4, setSettleMobileLast4] = useState('');
  const [settling, setSettling] = useState(false);
  const [settleError, setSettleError] = useState('');
  const [settleSuccess, setSettleSuccess] = useState('');
  const [ncReason, setNcReason] = useState('');
  const [ncAuthorisedBy, setNcAuthorisedBy] = useState('');
  const [settleGstin, setSettleGstin] = useState('');
  const [settleCompanyName, setSettleCompanyName] = useState('');
  const [settleCustomerAddr, setSettleCustomerAddr] = useState('');
  // Bill to Company (company from the Front Office Corporate Master)
  const BTC_NEW = { corporate_name: '', gst_number: '', contact_person: '', mobile_number: '', email: '', billing_address: '', credit_limit: '' };
  const [btcCompanies, setBtcCompanies] = useState(null); // null = not loaded
  const [btcCorpId, setBtcCorpId] = useState('');
  const [btcGuest, setBtcGuest] = useState('');
  const [btcRef, setBtcRef] = useState('');
  const [btcRemarks, setBtcRemarks] = useState('');
  const [btcNewOpen, setBtcNewOpen] = useState(false);
  const [btcNew, setBtcNew] = useState(BTC_NEW);
  const [btcBusy, setBtcBusy] = useState(false);
  const [btcMsg, setBtcMsg] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [itemQuantities, setItemQuantities] = useState({});
  const [cartItems, setCartItems] = useState([]);
  const [oomName, setOomName] = useState('');
  const [oomPrice, setOomPrice] = useState('');
  const [oomQty, setOomQty] = useState(1);
  const [selectedTableId, setSelectedTableId] = useState('');
  const [selectedStationId, setSelectedStationId] = useState('');
  const [selectedOrderType, setSelectedOrderType] = useState('DINE_IN');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [guestCount, setGuestCount] = useState(1);
  const [selectedTaxProfileId, setSelectedTaxProfileId] = useState('');
  const [selectedPricingProfileId, setSelectedPricingProfileId] = useState('');
  const [ordersPageSize, setOrdersPageSize] = useState(10);
  const [ordersPage, setOrdersPage] = useState(1);
  const [recentOrdersFilter, setRecentOrdersFilter] = useState('');
  const [recentOrdersTab, setRecentOrdersTab] = useState('ALL'); // 'ALL' | 'ROOM_SERVICE' | 'DINE_IN' | 'TAKE_AWAY' | 'DELIVERY'
  const [recentOrdersStatusFilter, setRecentOrdersStatusFilter] = useState('ALL'); // 'ALL' | 'PROCESSING' | 'BILLED' | 'SETTLED' | 'PENDING' | 'CANCELLED'
  const [kotsFilter, setKotsFilter] = useState('');
  const [recentOrdersViewIndex, setRecentOrdersViewIndex] = useState(0);
  const [kotsViewIndex, setKotsViewIndex] = useState(0);
  const cardsPerView = isMobile ? 1 : 4;
  const [tableSearchTerm, setTableSearchTerm] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  const [roomGuestName, setRoomGuestName] = useState('');
  const [roomGuestMeta, setRoomGuestMeta] = useState(null);
  const [roomGuestLoading, setRoomGuestLoading] = useState(false);
  const [isExternalGuest, setIsExternalGuest] = useState(false);
  const [propertyConfig, setPropertyConfig] = useState(null);

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // ── Bill modal action panels ──
  const [billAction, setBillAction] = useState(''); // 'cancel' | 'discount' | 'transfer'
  const [cancelReason, setCancelReason] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [discountType, setDiscountType] = useState('PERCENT');
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [discountBusy, setDiscountBusy] = useState(false);
  const [transferTableId, setTransferTableId] = useState('');
  const [transferBusy, setTransferBusy] = useState(false);

  // ── Step-based order flow ──
  const [step, setStep] = useState('service-type'); // 'service-type' | 'table' | 'room' | 'menu' | null
  const [selectedServiceType, setSelectedServiceType] = useState(''); // 'restaurant' | 'room-service' | 'takeaway'
  const [selectedTableObj, setSelectedTableObj] = useState(null);
  const [selectedRoomObj, setSelectedRoomObj] = useState(null);
  const [occupiedRooms, setOccupiedRooms] = useState([]);
  const [roomServiceOrders, setRoomServiceOrders] = useState([]);
  const [placingOrder, setPlacingOrder] = useState(false);

  // ── Edit Order state ──
  const [editingOrder, setEditingOrder] = useState(null); // { id, order_no, items: [] }
  const [editLoading, setEditLoading] = useState(false);

  // ── Collapsible section states ──
  const [orderSetupCollapsed, setOrderSetupCollapsed] = useState(false);
  const [guestInfoCollapsed, setGuestInfoCollapsed] = useState(false);
  const [tableCollapsed, setTableCollapsed] = useState(false);
  const tapMin = { minHeight: '44px' };

  // ── Table Session state ───────────────────────────────────────────────────
  const [activeTableSession, setActiveTableSession] = useState(null);
  const [tableSessionChecks, setTableSessionChecks] = useState([]);
  const [selectedCheckId, setSelectedCheckId] = useState('');
  const [blockingCheckCodes, setBlockingCheckCodes] = useState([]);
  const [sessionBusy, setSessionBusy] = useState(false);
  // Table linking
  const [linkedTableInfo, setLinkedTableInfo] = useState(null);
  // { is_primary, is_secondary, primary_session_id, primary_table, linked_tables:[{session_id,table_id,table_name}] }
  const [allTableSessions, setAllTableSessions] = useState([]);
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  // Combined linked orders modal
  const [linkedOrdersData, setLinkedOrdersData] = useState(null);
  // PAX + Steward modal (shown when selecting a table without an active session)
  const [showPaxModal, setShowPaxModal] = useState(false);
  const [paxModalTable, setPaxModalTable] = useState(null);
  const [paxInput, setPaxInput] = useState('');
  const [stewardInput, setStewardInput] = useState('');
  const [customerNameInput, setCustomerNameInput] = useState('');

  useEffect(() => {
    const onResize = () => setIsMobile(detectMobileUi());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  useEffect(() => {
    if (selectedServiceType === 'room-service' && isExternalGuest) {
      setIsExternalGuest(false);
    }
  }, [selectedServiceType, isExternalGuest]);

  const scopeHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': scope.chain_id,
    'x-property-id': scope.property_id,
    'x-outlet-id': scope.outlet_id
  });

  const clearStatus = () => {
    setError('');
    setMessage('');
  };

  const loadTables = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return setTables([]);
    try {
      const res = await fetch('/api/v1/kot/masters/tables', { headers: scopeHeaders() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load tables');
      const rows = Array.isArray(data) ? data : [];
      setTables(rows);
      if (!rows.length) {
        setSelectedTableId('');
      } else {
        const existsInOutlet = rows.some((t) => String(t.id) === String(selectedTableId));
        if (!existsInOutlet) setSelectedTableId(String(rows[0].id));
      }
    } catch {
      setError('Network error while loading tables');
    }
  };

  const loadMenuItems = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return setMenuItems([]);
    try {
      const res = await fetch('/api/v1/kot/masters/menu-items', { headers: scopeHeaders() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load menu items');
      const rows = Array.isArray(data) ? data : [];
      const normalized = rows.map((row) => ({
        ...row,
        name: String(row?.name || row?.menu_item_name || row?.item_name || '').trim(),
        sku: String(row?.sku || row?.item_code || '').trim(),
        category_name: String(row?.category_name || '').trim(),
        base_price: Number(row?.base_price ?? row?.rate ?? 0),
        effective_price: Number(row?.effective_price ?? row?.base_price ?? row?.rate ?? 0)
      }));
      setMenuItems(normalized);
      setItemQuantities((prev) => {
        const next = { ...prev };
        normalized.forEach((item) => { if (!next[item.id]) next[item.id] = 1; });
        return next;
      });
    } catch {
      setError('Network error while loading menu items');
    }
  };

  const loadStations = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return setStations([]);
    try {
      const res = await fetch('/api/v1/kot/masters/stations', { headers: scopeHeaders() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load kitchen stations');
      const rows = Array.isArray(data) ? data : [];
      setStations(rows);
      if (!rows.length) {
        setSelectedStationId('');
      } else {
        const existsInOutlet = rows.some((s) => String(s.id) === String(selectedStationId));
        if (!existsInOutlet) setSelectedStationId(String(rows[0].id));
      }
    } catch {
      setError('Network error while loading kitchen stations');
    }
  };

  const loadOrderTypes = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return setOrderTypes([]);
    try {
      const res = await fetch('/api/v1/kot/masters/order-types', { headers: scopeHeaders() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load order types');
      const rows = (Array.isArray(data) ? data : []).filter((r) => r && r.is_active !== false);
      setOrderTypes(rows);
      if (!rows.length) {
        setSelectedOrderType('DINE_IN');
        return;
      }
      const exists = rows.some((r) => String(r.order_type_code || '').toUpperCase() === String(selectedOrderType || '').toUpperCase());
      if (!exists) {
        const preferred = rows.find((r) => String(r.order_type_code || '').toUpperCase() === 'DINE_IN');
        setSelectedOrderType(String(preferred?.order_type_code || rows[0].order_type_code || 'DINE_IN'));
      }
    } catch {
      setError('Network error while loading order types');
    }
  };

  const loadBillingProfiles = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) {
      setTaxProfiles([]);
      setPricingProfiles([]);
      setSelectedTaxProfileId('');
      setSelectedPricingProfileId('');
      return;
    }
    try {
      const [taxRes, pricingRes] = await Promise.all([
        fetch('/api/v1/kot/masters/tax-profiles', { headers: scopeHeaders() }),
        fetch('/api/v1/kot/masters/pricing-profiles', { headers: scopeHeaders() })
      ]);
      const taxData = await taxRes.json().catch(() => ({}));
      const pricingData = await pricingRes.json().catch(() => ({}));
      if (!taxRes.ok) return setError(taxData.error || 'Failed to load tax profiles');
      if (!pricingRes.ok) return setError(pricingData.error || 'Failed to load pricing profiles');
      setTaxProfiles(Array.isArray(taxData) ? taxData : []);
      setPricingProfiles(Array.isArray(pricingData) ? pricingData : []);
    } catch {
      setError('Network error while loading billing profiles');
    }
  };

  const loadRecentOrders = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return setRecentOrders([]);
    try {
      const res = await fetch(`/api/v1/kot/orders?limit=50&_=${Date.now()}`, { headers: { ...scopeHeaders(), 'Cache-Control': 'no-cache' } });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load recent orders');
      setRecentOrders(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading recent orders');
    }
  };

  const loadKots = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return setKots([]);
    try {
      const res = await fetch(`/api/v1/kot/kots?_=${Date.now()}`, { headers: { ...scopeHeaders(), 'Cache-Control': 'no-cache' } });
      const serverDate = res.headers.get('Date');
      if (serverDate) serverTimeOffsetRef.current = new Date(serverDate).getTime() - Date.now();
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load KOT list');
      setKots(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading KOT list');
    }
  };

  useEffect(() => {
    if (selectedProperty?.chain_id && selectedProperty?.id) {
      setScope((prev) => ({ ...prev, chain_id: selectedProperty.chain_id, property_id: selectedProperty.id, outlet_id: String(selectedOutletId || '') }));
    } else {
      setScope({ chain_id: '', property_id: '', outlet_id: '' });
      setTables([]);
      setMenuItems([]);
      setStations([]);
      setTaxProfiles([]);
      setPricingProfiles([]);
      setRecentOrders([]);
      setKots([]);
      setPropertyConfig(null);
    }
  }, [selectedProperty]);

  useEffect(() => {
    if (!selectedProperty?.id) {
      setPropertyConfig(null);
      return;
    }
    fetch(`/api/v1/pms/property-config`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-chain-id': selectedProperty?.chain_id || '',
        'x-property-id': selectedProperty?.id || ''
      }
    })
      .then((res) => res.json())
      .then((data) => {
        if (data) setPropertyConfig(data);
      })
      .catch(() => {
        setPropertyConfig(null);
      });
  }, [selectedProperty?.id, token]);

  useEffect(() => {
    if (!selectedOutletId) return;
    setScope((prev) => ({ ...prev, outlet_id: String(selectedOutletId) }));
  }, [selectedOutletId]);

  useEffect(() => {
    if (!initialBillId) return;
    viewBill(initialBillId);
    onInitialBillConsumed && onInitialBillConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBillId]);

  useEffect(() => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return;
    setSelectedTaxProfileId('');
    setSelectedPricingProfileId('');
    loadTables();
    loadMenuItems();
    loadStations();
    loadOrderTypes();
    loadBillingProfiles();
    loadRecentOrders();
    loadKots();
    loadAllTableSessions();
    // Reset session state when outlet changes
    setActiveTableSession(null); setTableSessionChecks([]); setSelectedCheckId('');
    setLinkedTableInfo(null); setAllTableSessions([]); setLinkedOrdersData(null);
  }, [scope.outlet_id]);

  // Poll all open sessions every 10s for live occupancy
  useEffect(() => {
    if (!scope.outlet_id) return;
    const interval = setInterval(loadAllTableSessions, 10000);
    return () => clearInterval(interval);
  }, [scope.outlet_id]);

  // Poll KOTs + recent orders every 30s so Kitchen Tracker stays live
  useEffect(() => {
    if (!scope.outlet_id) return;
    const interval = setInterval(() => { loadKots(); loadRecentOrders(); }, 30000);
    return () => clearInterval(interval);
  }, [scope.outlet_id]);

  const menuCategories = useMemo(() => {
    const cats = [...new Set(menuItems.map(i => String(i.category_name || '').trim() || 'General').filter(Boolean))].sort();
    return ['ALL', ...cats];
  }, [menuItems]);

  const filteredMenuItems = useMemo(() => {
    const q = String(searchTerm || '').trim().toLowerCase();
    let items = menuItems;
    if (selectedCategory && selectedCategory !== 'ALL') {
      items = items.filter(i => (String(i.category_name || '').trim() || 'General') === selectedCategory);
    }
    if (!q) return items;
    return items.filter((item) => {
      const name = String(item?.name || '').toLowerCase();
      const sku = String(item?.sku || '').toLowerCase();
      const category = String(item?.category_name || '').toLowerCase();
      return name.includes(q) || sku.includes(q) || category.includes(q);
    });
  }, [menuItems, searchTerm, selectedCategory]);

  // Order-type tab bucketing. DINE_IN is the catch-all default (also covers
  // IN_ROOM_DINING and any unrecognized/legacy order_type) so no order can
  // silently fall outside every tab.
  const orderTypeBucket = (o) => {
    const t = String(o?.order_type || '').toUpperCase();
    if (t === 'ROOM_SERVICE') return 'ROOM_SERVICE';
    if (t === 'TAKE_AWAY' || t === 'TAKEAWAY') return 'TAKE_AWAY';
    if (t === 'DELIVERY') return 'DELIVERY';
    return 'DINE_IN';
  };
  // Status-row bucketing, derived from the same status/bill_status fields
  // already on each order — no backend change needed. PENDING means "food
  // ready, not yet billed"; BILLED means a bill exists but isn't settled yet.
  const orderStatusBucket = (o) => {
    const status = String(o?.status || '').toUpperCase();
    const billStatus = String(o?.bill_status || '').toUpperCase();
    if (status === 'CANCELLED' || billStatus === 'VOID') return 'CANCELLED';
    if (billStatus === 'SETTLED') return 'SETTLED';
    if (o?.bill_id) return 'BILLED';
    if (status === 'READY') return 'PENDING';
    return 'PROCESSING';
  };

  const recentOrdersByType = useMemo(() => {
    const buckets = { ROOM_SERVICE: [], DINE_IN: [], TAKE_AWAY: [], DELIVERY: [] };
    recentOrders.forEach((o) => { buckets[orderTypeBucket(o)].push(o); });
    return buckets;
  }, [recentOrders]);

  const typeScopedOrders = recentOrdersTab === 'ALL' ? recentOrders : recentOrdersByType[recentOrdersTab];

  const statusCounts = useMemo(() => {
    const counts = { ALL: typeScopedOrders.length, PROCESSING: 0, BILLED: 0, SETTLED: 0, PENDING: 0, CANCELLED: 0 };
    typeScopedOrders.forEach((o) => { counts[orderStatusBucket(o)] += 1; });
    return counts;
  }, [typeScopedOrders]);

  const filteredRecentOrders = useMemo(() => {
    let scoped = typeScopedOrders;
    if (recentOrdersStatusFilter !== 'ALL') {
      scoped = scoped.filter((o) => orderStatusBucket(o) === recentOrdersStatusFilter);
    }
    const q = String(recentOrdersFilter || '').trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter((o) => {
      const orderNo = String(o.order_no || '').toLowerCase();
      const tableFromMaster = tables.find((t) => String(t.id) === String(o.table_id))?.table_no || '';
      const tableNo = String(o.table_no || tableFromMaster || '').toLowerCase();
      const guest = String(o.guest_name || '').toLowerCase();
      return orderNo.includes(q) || tableNo.includes(q) || guest.includes(q);
    });
  }, [typeScopedOrders, recentOrdersStatusFilter, recentOrdersFilter, tables]);

  const totalOrderPages = Math.max(1, Math.ceil((filteredRecentOrders.length || 0) / Number(ordersPageSize || 10)));
  const pagedRecentOrders = useMemo(() => {
    const pageSize = Number(ordersPageSize || 10);
    const page = Math.min(Math.max(1, Number(ordersPage || 1)), Math.max(1, Math.ceil((filteredRecentOrders.length || 0) / pageSize)));
    const start = (page - 1) * pageSize;
    return filteredRecentOrders.slice(start, start + pageSize);
  }, [filteredRecentOrders, ordersPage, ordersPageSize]);

  const filteredKots = useMemo(() => {
    const q = String(kotsFilter || '').trim().toLowerCase();
    if (!q) return kots;
    return kots.filter((k) => {
      const orderNo = String(k.order_no || k.order_id || '').toLowerCase();
      const kotNo = String(k.kot_no || '').toLowerCase();
      const tableNo = String(k.table_no || '').toLowerCase();
      return orderNo.includes(q) || kotNo.includes(q) || tableNo.includes(q);
    });
  }, [kots, kotsFilter]);

  useEffect(() => {
    setOrdersPage(1);
  }, [ordersPageSize, scope.outlet_id, recentOrdersFilter, recentOrdersTab, recentOrdersStatusFilter]);

  useEffect(() => {
    setOrdersPage((prev) => Math.min(prev, totalOrderPages));
  }, [totalOrderPages]);

  useEffect(() => { setRecentOrdersViewIndex(0); }, [recentOrdersFilter, ordersPage, ordersPageSize, scope.outlet_id, recentOrdersTab, recentOrdersStatusFilter]);
  useEffect(() => { setRecentOrdersStatusFilter('ALL'); }, [recentOrdersTab]);
  useEffect(() => { setKotsViewIndex(0); }, [kotsFilter, scope.outlet_id]);

  const updateCardQty = (menuItemId, delta) => {
    const current = itemQuantities[menuItemId] || 1;
    setItemQuantities({ ...itemQuantities, [menuItemId]: Math.max(1, current + delta) });
  };

  const addToCart = (item) => {
    const qty = itemQuantities[item.id] || 1;
    const exists = cartItems.find((ci) => ci.menu_item_id === item.id);
    if (exists) {
      setCartItems(cartItems.map((ci) => ci.menu_item_id === item.id ? { ...ci, qty: ci.qty + qty } : ci));
    } else {
      setCartItems([
        ...cartItems,
        { menu_item_id: item.id, menu_item_name: item.name, unit_price: Number(item.effective_price || item.base_price || 0), qty, image_url: item.image_url || '', special_instructions: '' }
      ]);
    }
  };

  const updateCartInstructions = (menuItemId, value) => {
    setCartItems(cartItems.map((ci) => ci.menu_item_id === menuItemId ? { ...ci, special_instructions: value } : ci));
  };

  const updateCartPreference = (menuItemId, field, value) => {
    setCartItems(cartItems.map((ci) => ci.menu_item_id === menuItemId ? { ...ci, [field]: value || undefined } : ci));
  };

  const addOutOfMenuToCart = () => {
    if (!oomName.trim()) return setError('Enter item name for out-of-menu item');
    if (!oomPrice || Number(oomPrice) <= 0) return setError('Enter a valid price');
    const fakeId = `OOM_${Date.now()}`;
    setCartItems([
      ...cartItems,
      { menu_item_id: fakeId, menu_item_name: oomName.trim(), unit_price: Number(oomPrice), qty: Number(oomQty) || 1, image_url: '', special_instructions: '', is_out_of_menu: true }
    ]);
    setOomName(''); setOomPrice(''); setOomQty(1);
    clearStatus();
  };

  const updateCartQty = (menuItemId, delta) => {
    setCartItems(cartItems.map((ci) => ci.menu_item_id === menuItemId ? { ...ci, qty: Math.max(1, ci.qty + delta) } : ci));
  };

  const removeFromCart = (menuItemId) => setCartItems(cartItems.filter((ci) => ci.menu_item_id !== menuItemId));
  const clearTableCart = () => {
    const hasDraft = cartItems.length > 0 || Number(guestCount || 1) !== 1;
    if (!hasDraft) return;
    const confirmed = window.confirm('Clear cart for selected table?');
    if (!confirmed) return;
    setCartItems([]);
    setGuestCount(1);
    clearStatus();
  };

  // ── Edit Order functions ──
  const startEditOrder = async (order) => {
    setEditLoading(true); setError('');
    try {
      const res = await fetch(`/api/v1/kot/orders/${order.id}/items`, { headers: scopeHeaders() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load order items');
      const items = (Array.isArray(data) ? data : []).map(it => ({
        ...it, _origQty: Number(it.qty), _origRemarks: it.remarks || '',
      }));
      setEditingOrder({ id: order.id, order_no: order.order_no, status: order.status, items });
    } catch (err) { setError(err.message); }
    finally { setEditLoading(false); }
  };

  const editItemQty = (itemId, delta) => {
    setEditingOrder(prev => ({
      ...prev,
      items: prev.items.map(it => it.id === itemId ? { ...it, qty: Math.max(1, Number(it.qty) + delta) } : it),
    }));
  };

  const editItemRemarks = (itemId, val) => {
    setEditingOrder(prev => ({
      ...prev,
      items: prev.items.map(it => it.id === itemId ? { ...it, remarks: val } : it),
    }));
  };

  const removeEditItem = (itemId) => {
    setEditingOrder(prev => ({
      ...prev,
      items: prev.items.filter(it => it.id !== itemId),
      _removed: [...(prev._removed || []), itemId],
    }));
  };

  const saveEditOrder = async () => {
    if (!editingOrder) return;
    setEditLoading(true); setError(''); setMessage('');
    try {
      // Delete removed items
      for (const itemId of (editingOrder._removed || [])) {
        const r = await fetch(`/api/v1/kot/orders/${editingOrder.id}/items/${itemId}`, {
          method: 'DELETE', headers: scopeHeaders(),
        });
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Failed to remove item'); }
      }
      // Update changed items
      for (const it of editingOrder.items) {
        const qtyChanged = Number(it.qty) !== Number(it._origQty);
        const remarksChanged = (it.remarks || '') !== (it._origRemarks || '');
        if (qtyChanged || remarksChanged) {
          const body = {};
          if (qtyChanged) body.qty = Number(it.qty);
          if (remarksChanged) body.remarks = it.remarks || '';
          const r = await fetch(`/api/v1/kot/orders/${editingOrder.id}/items/${it.id}`, {
            method: 'PUT', headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
          if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Failed to update item'); }
        }
      }
      setMessage(`Order #${editingOrder.order_no} updated`);
      setEditingOrder(null);
      loadRecentOrders();
    } catch (err) { setError(err.message); }
    finally { setEditLoading(false); }
  };

  // ── Void Order ──
  const voidOrder = async (order) => {
    if (!order?.id) return;
    if (order.bill_status === 'SETTLED') return setError('Settled order cannot be voided');
    if (order.bill_status === 'VOID') return setError('Order is already voided');
    const comments = window.prompt(`Enter void reason for Order #${order.order_no || order.id}:`);
    if (comments === null) return;
    const trimmed = String(comments || '').trim();
    if (!trimmed) return setError('Void comments are required');
    setError(''); setMessage('');
    try {
      const res = await fetch(`/api/v1/kot/orders/${order.id}/void`, {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ comments: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to void order');
      setMessage(`Order #${order.order_no || order.id} voided successfully`);
      loadRecentOrders();
      loadKots();
    } catch (err) { setError(err.message); }
  };

  const subtotal = useMemo(() => cartItems.reduce((sum, ci) => sum + (ci.qty * ci.unit_price), 0), [cartItems]);
  // Cart-level "Apply Offer" discount — distributed proportionally across
  // each cart item's discount_amount when the order is created (that field
  // already flows through to order_subtotal on the backend), so this is a
  // real discount, not just a cosmetic total.
  const [cartDiscountType, setCartDiscountType] = useState('PERCENT');
  const [cartDiscountValue, setCartDiscountValue] = useState('');
  const cartDiscountAmount = useMemo(() => {
    const val = Number(cartDiscountValue) || 0;
    if (val <= 0 || subtotal <= 0) return 0;
    return cartDiscountType === 'PERCENT' ? subtotal * Math.min(100, val) / 100 : Math.min(val, subtotal);
  }, [cartDiscountType, cartDiscountValue, subtotal]);
  const [savingOrderDraft, setSavingOrderDraft] = useState(false);
  const filteredTables = useMemo(() => {
    const q = String(tableSearchTerm || '').trim().toLowerCase();
    if (!q) return tables;
    return tables.filter((t) => {
      const tableNo = String(t?.table_no || '').toLowerCase();
      const zone = String(t?.zone || '').toLowerCase();
      return tableNo.includes(q) || zone.includes(q);
    });
  }, [tables, tableSearchTerm]);
  const tableOccupancy = useMemo(() => {
    const map = {};
    const now = new Date();
    const isToday = (dateValue) => {
      const d = new Date(dateValue);
      if (Number.isNaN(d.getTime())) return false;
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
    };
    const activeStatuses = new Set(['OPEN', 'PARTIAL_KOT', 'KOT_SENT', 'PREPARING', 'READY']);
    for (const order of recentOrders) {
      const tableId = String(order?.table_id || '').trim();
      if (!tableId) continue;
      if (!isToday(order.opened_at || order.business_date)) continue;
      const status = String(order?.status || '').toUpperCase();
      const billStatus = String(order?.bill_status || '').toUpperCase();
      const occupied = activeStatuses.has(status) || billStatus === 'OPEN';
      const count = Number(order?.guest_count || 0);
      const ts = new Date(order?.opened_at || order?.business_date || 0).getTime() || 0;
      const existing = map[tableId];
      if (!existing || ts > existing.time) {
        map[tableId] = {
          occupied,
          count: count > 0 ? count : 0,
          time: ts,
          order_no: order?.order_no || ''
        };
      }
    }
    return map;
  }, [recentOrders]);
  const formatUpdatedAt = (value) => {
    if (!value) return '';
    return fmtTime(value);
  };

  // Returns true on success, false on any failure. Callers should branch on
  // the return value — do NOT rely on the `error` React state inside an async
  // continuation (it's a stale closure and yields false-positive success).
  // sendToKitchen=false is "Save Order" — creates the order + items (so the
  // cart survives a page reload) without alerting the kitchen.
  const createAndSendToKot = async ({ sendToKitchen = true } = {}) => {
    clearStatus();
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) { setError('Select property from header and choose outlet'); return false; }
    const orderTypeCode = String(selectedOrderType || '').toUpperCase();
    const selectedOrderTypeMeta = orderTypes.find(r => String(r.order_type_code || '').toUpperCase() === orderTypeCode);
    const requiresTable = selectedOrderTypeMeta?.table_required === true || selectedServiceType === 'restaurant';
    const isRoomService = selectedServiceType === 'room-service';
    const isDineIn = selectedServiceType === 'restaurant';
    const requiresRoomNumber = isRoomService;
    const requiresGuestLookup = isRoomService;
    if (requiresTable && !selectedTableId) { setError('Select table'); return false; }
    if (requiresRoomNumber) {
      if (!String(roomNumber || '').trim()) { setError('Enter room number'); return false; }
    }
    if (requiresGuestLookup) {
      if (!String(roomGuestName || '').trim()) { setError('Lookup guest name by room number'); return false; }
    }
    if (!selectedStationId) { setError('Select kitchen station'); return false; }
    if (cartItems.length === 0) { setError('Add at least one menu item to cart'); return false; }

    try {
      // Auto-open table session if one doesn't exist for this table
      let currentSessionId = activeTableSession?.id || null;
      let currentCheckId = selectedCheckId || null;
      if (requiresTable && selectedTableId && !currentSessionId) {
        try {
          const sessRes = await fetch('/api/table-sessions/open', {
            method: 'POST',
            headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ table_id: selectedTableId, outlet_id: scope.outlet_id || null })
          });
          const sessData = await sessRes.json().catch(() => ({}));
          if (sessRes.ok && sessData.session) {
            currentSessionId = sessData.session.id;
            setActiveTableSession(sessData.session);
            const checks = sessData.checks || [];
            setTableSessionChecks(checks);
            currentCheckId = checks[0]?.id || null;
            if (currentCheckId) setSelectedCheckId(currentCheckId);
          }
        } catch { /* proceed without session — order will still be created */ }
      }

      // Check if there's an existing open order for this table — reuse it instead of creating new.
      // Only reuse orders whose status accepts new items (see ORDER_STATUSES_ALLOW_SEND_TO_KOT in backend/v1/routes/kot.js).
      // A READY order on the same table is awaiting billing — leave it alone and start a fresh order.
      let orderId = null;
      if (requiresTable && selectedTableId) {
        const existingOrder = recentOrders.find(o =>
          String(o.table_id) === String(selectedTableId) &&
          ['OPEN', 'PARTIAL_KOT'].includes(o.status)
        );
        if (existingOrder) {
          orderId = existingOrder.id;
        }
      }

      // Create new order only if no existing open order for this table
      if (!orderId) {
        const orderRes = await fetch('/api/v1/kot/orders', {
          method: 'POST',
          headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order_type: orderTypeCode,
            table_id: requiresTable ? selectedTableId : null,
            guest_count: Number(guestCount || 1),
            tax_profile_id: selectedTaxProfileId || null,
            pricing_profile_id: selectedPricingProfileId || null,
            room_number: String(roomNumber || '').trim() || null,
            guest_name: isDineIn ? (String(customerNameInput || '').trim() || null) : (String(roomGuestName || '').trim() || null),
            mobile_last4: String(roomGuestMeta?.mobile_last4 || '').trim() || null,
            stay_id: Number(roomGuestMeta?.stay_id || 0) || null,
            reservation_id: Number(roomGuestMeta?.reservation_id || 0) || null,
            guest_type: isRoomService ? 'INHOUSE' : (isDineIn ? 'EXTERNAL' : null),
            session_id: currentSessionId,
            check_id: currentCheckId
          })
        });
        const orderData = await orderRes.json();
        if (!orderRes.ok) { setError(orderData.error || 'Create order failed'); return false; }
        orderId = orderData.id;
      }

      const postItem = async (id, ci) => {
        // "Apply Offer" cart discount, spread proportionally by line value —
        // real per-item discount_amount, not just a cosmetic total (the
        // backend's order_subtotal already sums (unit_price*qty - discount_amount)).
        const lineValue = Number(ci.qty || 0) * Number(ci.unit_price || 0);
        const itemDiscount = cartDiscountAmount > 0 && subtotal > 0
          ? Number((cartDiscountAmount * (lineValue / subtotal)).toFixed(2))
          : 0;
        return fetch(`/api/v1/kot/orders/${id}/items`, {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          menu_item_id: ci.is_out_of_menu ? 'OOM_' + Date.now() : ci.menu_item_id,
          qty: Number(ci.qty),
          unit_price: Number(ci.unit_price || 0),
          display_name: ci.is_out_of_menu ? ci.menu_item_name : undefined,
          discount_amount: itemDiscount,
          tax_amount: 0,
          remarks: ci.special_instructions || null,
          spice_level: ci.spice_level || null,
          portion_size: ci.portion_size || null
        })
        });
      };

      const createFreshOrder = async () => {
        const orderRes = await fetch('/api/v1/kot/orders', {
          method: 'POST',
          headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order_type: orderTypeCode,
            table_id: requiresTable ? selectedTableId : null,
            guest_count: Number(guestCount || 1),
            tax_profile_id: selectedTaxProfileId || null,
            pricing_profile_id: selectedPricingProfileId || null,
            room_number: String(roomNumber || '').trim() || null,
            guest_name: isDineIn ? (String(customerNameInput || '').trim() || null) : (String(roomGuestName || '').trim() || null),
            mobile_last4: String(roomGuestMeta?.mobile_last4 || '').trim() || null,
            stay_id: Number(roomGuestMeta?.stay_id || 0) || null,
            reservation_id: Number(roomGuestMeta?.reservation_id || 0) || null,
            guest_type: isRoomService ? 'INHOUSE' : (isDineIn ? 'EXTERNAL' : null),
            session_id: currentSessionId,
            check_id: currentCheckId
          })
        });
        const orderData = await orderRes.json();
        if (!orderRes.ok) { setError(orderData.error || 'Create order failed'); return null; }
        return orderData.id;
      };

      const createdItemIds = [];
      for (const ci of cartItems) {
        let itemRes = await postItem(orderId, ci);
        // Self-heal: if cached recentOrders thought this order was still open but server has since
        // moved it to READY/closed, drop the stale id, create a fresh order, and retry once.
        if (!itemRes.ok) {
          const itemErr = await itemRes.clone().json().catch(() => ({}));
          const msg = String(itemErr?.error || '');
          if (itemRes.status === 400 && /cannot accept new items|invalid|closed/i.test(msg) && createdItemIds.length === 0) {
            const freshId = await createFreshOrder();
            if (!freshId) return false;
            orderId = freshId;
            itemRes = await postItem(orderId, ci);
          }
        }
        const itemData = await itemRes.json();
        if (!itemRes.ok) { setError(itemData.error || 'Add item failed'); return false; }
        createdItemIds.push(itemData.id);
      }

      if (sendToKitchen) {
        const sendRes = await fetch(`/api/v1/kot/orders/${orderId}/send-to-kot`, {
          method: 'POST',
          headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ station_id: selectedStationId, order_item_ids: createdItemIds })
        });
        const sendData = await sendRes.json();
        if (!sendRes.ok) { setError(sendData.error || 'Send to KOT failed'); return false; }

        if (sendData.idempotent_replay) {
          setMessage(`Items added to existing order — KOT #${sendData.kot_no} reused`);
        } else {
          setMessage(`Order sent to KOT #${sendData.kot_no}`);
        }
        window.dispatchEvent(new CustomEvent('kot-raised', {
          detail: {
            chain_id: scope.chain_id,
            property_id: scope.property_id,
            outlet_id: scope.outlet_id,
            station_id: selectedStationId,
            kot_id: sendData.id || null
          }
        }));
        // Auto-open the KOT print dialog so staff can hand the slip to the chef
        if (sendData.id) {
          printKot(sendData.id);
        }
      } else {
        setMessage('Order saved — items added, kitchen not notified yet');
      }
      // Auto-collapse setup sections after successful order
      setOrderSetupCollapsed(true);
      setGuestInfoCollapsed(true);
      setTableCollapsed(true);
      setCartItems([]);
      setCartDiscountValue('');
      loadRecentOrders();
      loadKots();
      loadAllTableSessions();
      if (selectedTableId) loadTableSession(selectedTableId);
      return true;
    } catch {
      setError('Network error while placing order');
      return false;
    }
  };

  const openGeneratePreview = async (orderId) => {
    clearStatus();
    const order = recentOrders.find(o => o.id === orderId) || null;
    try {
      const res = await fetch(`/api/v1/kot/orders/${orderId}/items`, { headers: scopeHeaders() });
      const items = res.ok ? await res.json() : [];
      setGeneratePreview({
        orderId,
        order,
        items: Array.isArray(items) ? items : [],
        discountType: 'NONE',
        discountValue: '',
        generating: false,
      });
    } catch {
      setError('Failed to load order items');
    }
  };

  const confirmGenerateBill = async () => {
    if (!generatePreview?.orderId) return;
    setGeneratePreview(prev => prev ? { ...prev, generating: true } : prev);
    clearStatus();
    try {
      const body = {};
      const dv = Number(generatePreview.discountValue);
      if (generatePreview.discountType !== 'NONE' && Number.isFinite(dv) && dv > 0) {
        body.discount_type = generatePreview.discountType;
        body.discount_value = dv;
      }
      const res = await fetch(`/api/v1/kot/orders/${generatePreview.orderId}/generate-bill`, {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setGeneratePreview(prev => prev ? { ...prev, generating: false } : prev);
        return setError(data.error || 'Generate bill failed');
      }
      setMessage(`Bill generated: Bill No ${data.bill_no}`);
      setGeneratePreview(null);
      refreshAll();
      if (data.id || data.bill_id) {
        viewBill(data.id || data.bill_id);
      }
    } catch {
      setGeneratePreview(prev => prev ? { ...prev, generating: false } : prev);
      setError('Network error while generating bill');
    }
  };

  // Open the consolidated table bill preview — loads all items across all orders
  const handleGenerateTableBill = async (table, orderIds) => {
    clearStatus();
    try {
      // Fetch items for all orders in one go using the consolidate preview
      // We collect items from each order individually and merge them
      const fetches = orderIds.map(oid =>
        fetch(`/api/v1/kot/orders/${oid}/items`, { headers: scopeHeaders() })
          .then(r => r.ok ? r.json() : [])
      );
      const allItemArrays = await Promise.all(fetches);
      // Merge items: group by menu_item_name + unit_price, sum qty
      const merged = {};
      for (const arr of allItemArrays) {
        for (const it of (Array.isArray(arr) ? arr : [])) {
          const key = `${it.menu_item_name}__${it.unit_price}`;
          if (!merged[key]) {
            merged[key] = { ...it, qty: 0, line_total: 0, discount_amount: 0, cgst_amount: 0, sgst_amount: 0, igst_amount: 0, tax_amount: 0 };
          }
          merged[key].qty += Number(it.qty || 0);
          merged[key].line_total += Number(it.line_total || 0);
          merged[key].discount_amount += Number(it.discount_amount || 0);
          merged[key].cgst_amount += Number(it.cgst_amount || 0);
          merged[key].sgst_amount += Number(it.sgst_amount || 0);
          merged[key].igst_amount += Number(it.igst_amount || 0);
          merged[key].tax_amount += Number(it.tax_amount || 0);
        }
      }
      setTableBillPreview({
        table,
        orderIds,
        items: Object.values(merged),
        discountType: 'NONE',
        discountValue: '',
        generating: false,
      });
    } catch {
      setError('Failed to load table order items');
    }
  };

  // Print the most recent KOT for a table — called from the table grid card
  const handlePrintKotFromCard = async (table) => {
    const tid = String(table.id);
    const tableOrderIds = new Set(recentOrders.filter(o => String(o.table_id) === tid).map(o => String(o.id)));
    const tableKots = kots.filter(k => tableOrderIds.has(String(k.order_id)));
    if (!tableKots.length) { setError('No KOT found for this table'); return; }
    const latest = tableKots.sort((a, b) => new Date(b.raised_at) - new Date(a.raised_at))[0];
    try {
      await printKotById(latest.id, scopeHeaders(), selectedProperty?.property_name || '');
    } catch (e) {
      setError(String(e?.message || 'Failed to print KOT'));
    }
  };

  const confirmTableBill = async () => {
    if (!tableBillPreview?.orderIds?.length) return;
    setTableBillPreview(prev => prev ? { ...prev, generating: true } : prev);
    clearStatus();
    try {
      const body = { order_ids: tableBillPreview.orderIds, table_id: tableBillPreview.table?.id };
      const dv = Number(tableBillPreview.discountValue);
      if (tableBillPreview.discountType !== 'NONE' && Number.isFinite(dv) && dv > 0) {
        body.discount_type = tableBillPreview.discountType;
        body.discount_value = dv;
      }
      const res = await fetch('/api/v1/kot/table-bill/consolidate', {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setTableBillPreview(prev => prev ? { ...prev, generating: false } : prev);
        return setError(data.error || 'Generate table bill failed');
      }
      setMessage(`Table bill generated: ${data.bill_no}`);
      setTableBillPreview(null);
      refreshAll();
      if (data.id || data.bill_id) {
        viewBill(data.id || data.bill_id);
      }
    } catch {
      setTableBillPreview(prev => prev ? { ...prev, generating: false } : prev);
      setError('Network error while generating table bill');
    }
  };

  const kotStatusAction = async (id, action) => {
    clearStatus();
    try {
      const res = await fetch(`/api/v1/kot/kots/${id}/${action}`, { method: 'POST', headers: scopeHeaders() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || `KOT ${action} failed`);
      setMessage(`KOT ${action} success`);
      refreshAll();
    } catch {
      setError(`Network error while KOT ${action}`);
    }
  };

  const toMoney = (value) => Number(value || 0).toFixed(2);

  const buildKotPrintHtml = (payload) => buildKotPrintHtmlShared(payload, selectedProperty?.name || '');

  const fetchBillDetail = async (billId) => {
    const res = await fetch(`/api/v1/kot/bills/${billId}`, { headers: scopeHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Failed to load bill');
    return data;
  };

  const fetchKotPrint = async (kotId) => {
    const res = await fetch(`/api/v1/kot/kots/${kotId}/print`, { headers: scopeHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Failed to load KOT print');
    return data;
  };

  const viewBill = async (billId) => {
    clearStatus();
    try {
      const data = await fetchBillDetail(billId);
      setBillPreview(data);
    } catch (err) {
      setError(err?.message || 'Failed to load bill');
    }
  };

  const openSettlePanel = () => {
    const grand = Number(billPreview?.bill?.grand_total || 0);
    setSettlePanel(true);
    setSettleMode('CASH');
    setSettleAmount(grand.toFixed(2));
    setSettleRef('');
    setSettleRoomNumber('');
    setSettleGuestName('');
    setSettleMobileLast4('');
    setSettleError('');
    setSettleSuccess('');
    setBtcCorpId('');
    setBtcGuest('');
    setBtcRef('');
    setBtcRemarks('');
    setBtcNewOpen(false);
    setBtcMsg('');
  };

  const btcHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    'x-outlet-id': selectedOutletId,
  });
  const loadBtcCompanies = async () => {
    try {
      const res = await fetch('/api/v1/kot/companies', { headers: btcHeaders() });
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data?.error || 'Failed to load companies');
      setBtcCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      setBtcCompanies([]);
      setBtcMsg(err?.message || 'Failed to load companies');
    }
  };
  const chooseSettleMode = (key) => {
    setSettleMode(key);
    if (key === 'BILL_TO_COMPANY') loadBtcCompanies();
  };
  const saveBtcCompany = async () => {
    const n = btcNew;
    if (!n.corporate_name.trim()) { setBtcMsg('Enter the company name'); return; }
    const g = n.gst_number.trim().toUpperCase();
    if (g && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(g)) { setBtcMsg('Enter a valid 15-character GSTIN or leave it blank'); return; }
    setBtcBusy(true);
    setBtcMsg('');
    try {
      const res = await fetch('/api/v1/kot/companies', {
        method: 'POST',
        headers: { ...btcHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...n, gst_number: g, credit_limit: n.credit_limit === '' ? 0 : Number(n.credit_limit) }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409 && data?.corporate_id) {
        await loadBtcCompanies();
        setBtcCorpId(String(data.corporate_id));
        setBtcNewOpen(false);
        setBtcMsg(data.error || 'Company already exists — selected it');
        return;
      }
      if (!res.ok) throw new Error(data?.error || 'Failed to save company');
      setBtcCompanies((list) => [...(list || []), data].sort((a, b) => String(a.corporate_name).localeCompare(String(b.corporate_name))));
      setBtcCorpId(String(data.corporate_id));
      setBtcNewOpen(false);
      setBtcNew(BTC_NEW);
    } catch (err) {
      setBtcMsg(err?.message || 'Failed to save company');
    } finally {
      setBtcBusy(false);
    }
  };

  const handleSettle = async () => {
    const bill = billPreview?.bill;
    if (!bill?.id) return;
    setSettling(true);
    setSettleError('');
    setSettleSuccess('');
    try {
      const isRoom = settleMode === 'ROOM_CHARGE';
      const isComp = settleMode === 'COMPLIMENTARY';
      const isBtc = settleMode === 'BILL_TO_COMPANY';
      if (isBtc) {
        if (!btcCorpId) throw new Error('Select the company to bill');
        if (!btcGuest.trim()) throw new Error('Enter the guest / employee name');
      }

      // Step 1: the tender (non-room-charge, non-complimentary) goes with the
      // settle request so the server records it in the same transaction — a
      // failed settle no longer leaves a payment behind for the retry to double.
      let paymentForSettle = null;
      if (!isRoom && !isComp && !isBtc) {
        const payAmount = Number(settleAmount || 0);
        if (payAmount <= 0) throw new Error('Enter a valid amount');
        paymentForSettle = { payment_mode: settleMode, amount: payAmount, reference_no: settleRef || null };
      }

      // Validate NC fields before settling
      if (isComp) {
        if (!ncReason) throw new Error('Select a reason for the complimentary bill');
        if (!ncAuthorisedBy.trim()) throw new Error('Enter the name of the authorising manager');
      }

      // Step 2: Settle the bill
      const settleBody = {
        settlement_mode: isRoom ? 'PAY_AT_CHECKOUT' : 'DIRECT_PAYMENT',
        ...(paymentForSettle ? { payment: paymentForSettle } : {}),
        ...(isRoom ? { room_number: settleRoomNumber, guest_name: settleGuestName, mobile_last4: settleMobileLast4 } : {}),
        ...(isComp ? { is_complimentary: true, nc_reason: ncReason, nc_authorised_by: ncAuthorisedBy.trim() } : {}),
        ...(isBtc ? { bill_to_company: { corporate_id: Number(btcCorpId), guest_name: btcGuest.trim(), reference_no: btcRef.trim(), remarks: btcRemarks.trim() } } : {}),
        ...(settleGstin ? { customer_gstin: settleGstin.toUpperCase() } : {}),
        ...(settleCompanyName.trim() ? { customer_name_bill: settleCompanyName.trim() } : {}),
        ...(settleCustomerAddr.trim() ? { customer_address: settleCustomerAddr.trim() } : {}),
      };
      const settleRes = await fetch(`/api/v1/kot/bills/${bill.id}/settle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'x-chain-id': selectedProperty?.chain_id || '', 'x-property-id': selectedProperty?.id || '', 'x-outlet-id': selectedOutletId },
        body: JSON.stringify(settleBody)
      });
      const settleData = await settleRes.json().catch(() => ({}));
      if (!settleRes.ok) throw new Error(settleData.error || 'Failed to settle bill');

      setSettleSuccess(isBtc
        ? `Bill #${bill.bill_no} billed to ${(btcCompanies || []).find((c) => String(c.corporate_id) === String(btcCorpId))?.corporate_name || 'the company'} — nothing collected now`
        : `Bill #${bill.bill_no} settled via ${settleMode.replace(/_/g, ' ')}`);
      setSettlePanel(false);
      refreshAll();
    } catch (err) {
      setSettleError(err.message);
    } finally {
      setSettling(false);
    }
  };

  const closeBillAction = () => {
    setBillAction('');
    setCancelReason('');
    setDiscountValue('');
    setTransferTableId('');
  };

  const handleCancelBill = async () => {
    if (!cancelReason.trim()) return setError('Please enter a reason for cancellation');
    const orderId = billPreview?.bill?.order_id;
    if (!orderId) return;
    setCancelBusy(true);
    try {
      const res = await fetch(`/api/v1/kot/orders/${orderId}/void`, {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ comments: cancelReason }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to cancel bill');
      setMessage('Bill cancelled successfully');
      setBillPreview(null);
      closeBillAction();
      await Promise.all([loadKots(), loadRecentOrders()]);
    } catch { setError('Network error while cancelling bill'); }
    finally { setCancelBusy(false); }
  };

  const handleApplyDiscount = async () => {
    const dv = Number(discountValue);
    if (!discountValue || !Number.isFinite(dv) || dv <= 0) return setError('Enter a valid discount value');
    const billId = billPreview?.bill?.id;
    const orderId = billPreview?.bill?.order_id;
    if (!billId || !orderId) return;
    setDiscountBusy(true);
    try {
      const res = await fetch(`/api/v1/kot/orders/${orderId}/apply-discount`, {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount_type: discountType, discount_value: dv, discount_reason: discountReason.trim() || null, discount_by: scope.username || '' }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to apply discount');
      setMessage('Discount applied');
      closeBillAction();
      const updated = await fetchBillDetail(billId);
      setBillPreview(updated);
    } catch { setError('Network error while applying discount'); }
    finally { setDiscountBusy(false); }
  };

  const handleTableTransfer = async () => {
    if (!transferTableId) return setError('Select a table');
    const orderId = billPreview?.bill?.order_id;
    if (!orderId) return;
    setTransferBusy(true);
    try {
      const res = await fetch(`/api/v1/kot/orders/${orderId}/transfer-table`, {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ table_id: transferTableId }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to transfer table');
      setMessage(`Table transferred to ${data.table_no || transferTableId}`);
      closeBillAction();
      const billId = billPreview?.bill?.id;
      const updated = await fetchBillDetail(billId);
      setBillPreview(updated);
      await loadKots();
    } catch { setError('Network error while transferring table'); }
    finally { setTransferBusy(false); }
  };

  const handleExemption = () => {
    closeBillAction();
    setSettleMode('COMPLIMENTARY');
    setSettlePanel(true);
  };

  const printBill = async (billId) => {
    clearStatus();
    try {
      const data = await fetchBillDetail(billId);
      openBillPrintWindow(data);
    } catch (err) {
      setError(err?.message || 'Failed to print bill');
    }
  };

  const viewKot = async (kotId) => {
    clearStatus();
    try {
      const data = await fetchKotPrint(kotId);
      setKotPreview(data);
    } catch (err) {
      setError(err?.message || 'Failed to load KOT');
    }
  };

  const printKot = async (kotId) => {
    clearStatus();
    try {
      const data = await fetchKotPrint(kotId);
      const w = window.open('', '_blank', 'width=700,height=760');
      if (!w) return setError('Enable popups to print KOT');
      w.document.write(buildKotPrintHtml(data));
      w.document.close();
      w.focus();
      w.print();
    } catch (err) {
      setError(err?.message || 'Failed to print KOT');
    }
  };

  // ── UI theme ──────────────────────────────────────────────────────────────
  const C = {
    primary: '#1e3a5f', accent: '#3b82f6', success: '#22c55e', warn: '#f59e0b',
    danger: '#ef4444', bg: 'var(--theme-border-light)', cardBg: '#ffffff', border: 'var(--theme-border)',
    text: '#1e293b', muted: 'var(--theme-text-secondary)', headerBg: '#1e3a5f', headerText: '#ffffff'
  };
  const inputStyle = { ...tapMin, padding: '0.55rem 0.7rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: isMobile ? '16px' : '14px', background: '#fff', color: C.text, width: '100%', boxSizing: 'border-box' };
  const btnStyle = { ...tapMin, padding: '0.55rem 1rem', border: 'none', borderRadius: '8px', background: C.primary, color: '#fff', cursor: 'pointer', fontSize: isMobile ? '15px' : '14px', fontWeight: 600 };
  const card = { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '0.85rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' };
  const secHead = { margin: '0 0 0.5rem', fontSize: '0.82rem', fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.35rem', borderBottom: `2px solid ${C.border}` };
  const collapsibleHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none', margin: '0 0 0.5rem', fontSize: '0.82rem', fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.35rem', borderBottom: `2px solid ${C.border}` };
  const chevron = (collapsed) => <span style={{ fontSize: '0.7rem', color: C.muted, transition: 'transform 0.15s', marginLeft: '0.4rem' }}>{collapsed ? '▸' : '▾'}</span>;
  const collapsedSummary = (text) => <div style={{ fontSize: '0.8rem', color: C.muted, fontWeight: 500, padding: '0.1rem 0' }}>{text}</div>;

  const tableNameById = (id) => tables.find((t) => String(t.id) === String(id))?.table_no || '-';
  const stationNameById = (id) => stations.find((s) => String(s.id) === String(id))?.name || id || '-';
  // Generate Bill is only allowed when ALL KOTs are served (status = READY).
  // PARTIAL_KOT means KOT was raised but not yet served by kitchen.
  // KOT_SENT means KOT is acknowledged but kitchen hasn't served yet.
  const canGenerateBill = (order) =>
    !order.bill_id && order.status === 'READY';

  // Set of table IDs where all active orders are READY and unbilled — these
  // use consolidated table billing (from the table card), not per-order bill.
  const tablesUsingConsolidatedBill = useMemo(() => {
    const activeStatuses = new Set(['OPEN', 'PARTIAL_KOT', 'KOT_SENT', 'PREPARING', 'READY']);
    const grouped = {};
    for (const o of recentOrders) {
      const tid = String(o.table_id || '');
      if (!tid) continue;
      const st = String(o.status || '').toUpperCase();
      if (!activeStatuses.has(st)) continue;
      if (!grouped[tid]) grouped[tid] = [];
      grouped[tid].push(o);
    }
    const s = new Set();
    for (const [tid, orders] of Object.entries(grouped)) {
      // Consolidated table-level billing only applies when the table has
      // MULTIPLE ready unbilled orders that need a single combined bill.
      // A single ready order can be billed directly from its Recent Orders
      // tile — no need to bounce the user over to the table card UI.
      if (orders.length > 1 && orders.every(o => o.status === 'READY') && orders.every(o => !o.bill_id)) {
        s.add(tid);
      }
    }
    return s;
  }, [recentOrders]);
  const canStartKot = (kot) => kot.status === 'RAISED';
  const canReadyKot = (kot) => kot.status === 'PREPARING';
  const canServeKot = (kot) => kot.status === 'READY';
  const refreshAll = async () => {
    if (refreshing) return;
    setRefreshing(true);
    clearStatus();
    try {
      await Promise.all([loadRecentOrders(), loadKots(), loadAllTableSessions()]);
      setOrdersPage(1);
      setRecentOrdersViewIndex(0);
      setKotsViewIndex(0);
    } finally {
      setRefreshing(false);
    }
  };

  const lookupInhouseGuest = async () => {
    clearStatus();
    const roomNo = String(roomNumber || '').trim();
    if (!roomNo) return setError('Enter room number first');
    setRoomGuestLoading(true);
    try {
      const res = await fetch(`/api/v1/kot/inhouse-guests?room_number=${encodeURIComponent(roomNo)}`, { headers: scopeHeaders() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) return setError(data?.error || 'Guest lookup failed');
      const row = Array.isArray(data) ? data[0] : null;
      if (!row) {
        setRoomGuestName('');
        setRoomGuestMeta(null);
        return setError('No checked-in guest found for this room');
      }
      setRoomGuestName(row.primary_guest_name || '');
      setRoomGuestMeta({
        stay_id: row.stay_id || null,
        reservation_id: row.reservation_id || null,
        mobile_last4: row.mobile_last4 || null
      });
      setGuestInfoCollapsed(true); // auto-collapse after verification
    } catch {
      setError('Guest lookup failed');
    } finally {
      setRoomGuestLoading(false);
    }
  };

  // ── Room Service: load occupied rooms ──
  const loadOccupiedRooms = async () => {
    if (!scope.chain_id || !scope.property_id) return;
    try {
      const res = await fetch('/api/v1/pms/room-rack/live', { headers: scopeHeaders() });
      const data = await res.json();
      if (!res.ok) return;
      const rows = Array.isArray(data) ? data : (data?.rooms || []);
      // Backend returns occupancy_status (not status) and primary_guest_name (not guest_name).
      // Normalise to the shape RoomSelector expects, and only show rooms that are
      // serviceable for room service — occupied (in-house) and not blocked/out-of-order.
      // Shared rule (Bug 8720) so the KOT and Bar order flows can't drift apart.
      setOccupiedRooms(
        rows
          .filter(isRoomServiceEligible)
          .map(r => ({
            ...r,
            status: r.occupancy_status || '',
            guest_name: r.primary_guest_name || r.guest_name || '',
            is_occupied: true
          }))
      );
    } catch { /* silent */ }
  };

  const loadRoomServiceOrders = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return;
    try {
      const res = await fetch('/api/v1/kot/orders?limit=200&order_type=ROOM_SERVICE', { headers: scopeHeaders() });
      const data = await res.json();
      if (!res.ok) return;
      const rows = (Array.isArray(data) ? data : []).filter(o => {
        const st = String(o.status || '').toUpperCase();
        const bs = String(o.bill_status || '').toUpperCase();
        return st !== 'VOID' && bs !== 'VOID' && bs !== 'SETTLED';
      });
      setRoomServiceOrders(rows);
    } catch { /* silent */ }
  };

  // ── Step flow handlers ──
  const handleServiceTypeSelect = (type) => {
    setSelectedServiceType(type);
    if (type === 'restaurant') {
      // Prefer exact DINE_IN code, then any table_required type, then any name match
      const tableOrderType =
        orderTypes.find(r => String(r.order_type_code || '').toUpperCase() === 'DINE_IN') ||
        orderTypes.find(r => r.table_required === true) ||
        orderTypes.find(r => /dine|restaurant/i.test(r.order_type_name || r.order_type_code)) ||
        null;
      setSelectedOrderType(String(tableOrderType?.order_type_code || 'DINE_IN'));
      setIsExternalGuest(true);
      setRoomNumber('');
      setRoomGuestName('');
      setRoomGuestMeta(null);
      setStep('table');
    } else if (type === 'room-service') {
      const roomOrderType =
        orderTypes.find(r => String(r.order_type_code || '').toUpperCase() === 'ROOM_SERVICE') ||
        orderTypes.find(r => /room.?service/i.test(r.order_type_name || r.order_type_code)) ||
        null;
      setSelectedOrderType(String(roomOrderType?.order_type_code || 'ROOM_SERVICE'));
      setIsExternalGuest(false);
      setSelectedTableId('');
      setSelectedTableObj(null);
      loadOccupiedRooms();
      loadRoomServiceOrders();
      setStep('room');
    } else if (type === 'takeaway') {
      const takeawayOrderType =
        orderTypes.find(r => String(r.order_type_code || '').toUpperCase() === 'TAKE_AWAY') ||
        orderTypes.find(r => /take.?away/i.test(r.order_type_name || r.order_type_code)) ||
        null;
      setSelectedOrderType(String(takeawayOrderType?.order_type_code || 'TAKE_AWAY'));
      setIsExternalGuest(true);
      setSelectedTableId('');
      setSelectedTableObj(null);
      setStep('menu');
    }
  };

  const handleTableSelect = async (table) => {
    setSelectedTableObj(table);
    setSelectedTableId(String(table.id));
    setCustomerNameInput('');

    // Check if table already has an active session
    const hasSession = allTableSessions.some(s =>
      (String(s.table_id) === String(table.id) || String(s.kot_table_id) === String(table.id)) && s.status === 'OPEN'
    );
    if (hasSession) {
      // Occupied table — load session directly, skip PAX modal
      await loadTableSession(String(table.id));
      setStep('menu');
    } else {
      // Available table — show PAX + Steward modal first
      setPaxModalTable(table);
      setPaxInput('');
      setStewardInput(String(activeUser || ''));
      setShowPaxModal(true);
    }
  };

  const activeUser = typeof window !== 'undefined' ? '' : '';

  const handlePaxModalConfirm = async () => {
    if (!paxModalTable) return;
    setShowPaxModal(false);
    // Sync the order's guest count to the PAX entered for the table session,
    // otherwise the order is created with the default guest_count=1 and the
    // table tile / bill show the wrong PAX (Bug 8725).
    setGuestCount(Number(paxInput) || 1);
    const tableId = String(paxModalTable.id);
    // Open session with PAX + Steward
    try {
      const res = await fetch('/api/table-sessions/open', {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          table_id: tableId,
          outlet_id: scope.outlet_id || null,
          pax: Number(paxInput) || 1,
          steward_name: stewardInput.trim() || null,
          customer_name: customerNameInput.trim() || null
        })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.session) {
        setActiveTableSession(data.session);
        setTableSessionChecks(data.checks || []);
        setSelectedCheckId(data.checks?.[0]?.id || '');
        setLinkedTableInfo(null);
        loadAllTableSessions();
      }
    } catch { /* proceed without session */ }
    setStep('menu');
  };

  const handlePaxModalCancel = () => {
    setShowPaxModal(false);
    setPaxModalTable(null);
    setCustomerNameInput('');
  };

  const handleRoomSelect = (room) => {
    setSelectedRoomObj(room);
    setRoomNumber(String(room.room_number || ''));
    setRoomGuestName(room.guest_name || room.primary_guest_name || '');
    setRoomGuestMeta({
      stay_id: room.stay_id || null,
      reservation_id: room.reservation_id || null,
      mobile_last4: room.mobile_last4 || null,
    });
    setStep('menu');
  };

  const handleMenuPlaceOrder = async () => {
    setPlacingOrder(true);
    const ok = await createAndSendToKot();
    setPlacingOrder(false);
    // Only navigate away after a confirmed success — the `error` state is
    // a stale closure here and would yield false-positive navigation on
    // failures (button click would feel like "nothing happened").
    if (ok) {
      if (selectedServiceType === 'room-service') { loadRoomServiceOrders(); setStep('room'); }
      else if (selectedServiceType === 'takeaway') setStep('service-type');
      // Restaurant: stay on menu so staff can add more items or use the action bar
    }
  };

  const handleSaveOrderDraft = async () => {
    setSavingOrderDraft(true);
    await createAndSendToKot({ sendToKitchen: false });
    setSavingOrderDraft(false);
  };

  // Reprint the most recently raised KOT for whichever order(s) belong to
  // the table/room currently open in Menu & Order — same slip a "Print KOT"
  // action on Recent Orders/KOT Tracker would produce, just reachable
  // without leaving this screen.
  const handlePrintLatestKotForTable = () => {
    const orderIds = new Set(
      recentOrders
        .filter(o => selectedTableId && String(o.table_id) === String(selectedTableId))
        .map(o => String(o.id))
    );
    if (!orderIds.size) { setError('No order found for this table yet'); return; }
    const latest = kots
      .filter(k => orderIds.has(String(k.order_id)))
      .sort((a, b) => new Date(b.raised_at || 0) - new Date(a.raised_at || 0))[0];
    if (!latest) { setError('No KOT has been raised for this table yet'); return; }
    printKot(latest.id);
  };

  const handleMenuGuestDetails = () => {
    if (!selectedTableObj) return;
    setPaxModalTable(selectedTableObj);
    setPaxInput(String(activeTableSession?.pax || guestCount || ''));
    setStewardInput(String(activeTableSession?.steward_name || ''));
    setShowPaxModal(true);
  };

  const handleMenuBack = () => {
    if (selectedServiceType === 'restaurant') setStep('table');
    else if (selectedServiceType === 'room-service') setStep('room');
    else setStep('service-type');
  };

  const handleMenuTableTransfer = async (targetTableId) => {
    if (!targetTableId || !selectedTableId) return;
    const activeOrders = recentOrders.filter(o =>
      String(o.table_id) === String(selectedTableId) &&
      ['OPEN', 'PARTIAL_KOT', 'KOT_SENT', 'PREPARING', 'READY'].includes(o.status)
    );
    if (!activeOrders.length) return setError('No active orders to transfer');
    try {
      const results = await Promise.all(activeOrders.map(o =>
        fetch(`/api/v1/kot/orders/${o.id}/transfer-table`, {
          method: 'POST',
          headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ table_id: targetTableId }),
        }).then(r => r.json())
      ));
      const err = results.find(r => r.error);
      if (err) return setError(err.error || 'Transfer failed');
      const targetTable = tables.find(t => String(t.id) === String(targetTableId));
      setMessage(`Orders transferred to ${targetTable?.table_no || 'new table'}`);
      setSelectedTableId(String(targetTableId));
      setSelectedTableObj(targetTable || null);
      await Promise.all([loadRecentOrders(), loadAllTableSessions()]);
      await loadTableSession(String(targetTableId));
    } catch { setError('Network error during table transfer'); }
  };

  const handleMenuGenerateBill = () => {
    const today = new Date().toISOString().slice(0, 10);
    const active = recentOrders.filter(o => {
      if (String(o.table_id) !== String(selectedTableId)) return false;
      if ((o.business_date || o.opened_at || '').toString().slice(0, 10) !== today) return false;
      return ['OPEN', 'PARTIAL_KOT', 'KOT_SENT', 'PREPARING', 'READY'].includes(o.status);
    });
    const billable = active.filter(o => !o.bill_id);
    if (billable.length === 0) return setError('No active unbilled orders found for this table');
    if (billable.length === 1 && active.length === 1) {
      openGeneratePreview(billable[0].id);
    } else {
      handleGenerateTableBill(selectedTableObj, billable.map(o => o.id));
    }
  };

  // ── Table Session functions ───────────────────────────────────────────────

  const loadAllTableSessions = async () => {
    if (!scope.property_id) return;
    try {
      const res = await fetch(`/api/table-sessions?status=OPEN`, { headers: scopeHeaders() });
      const data = await res.json().catch(() => []);
      setAllTableSessions(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };

  const loadTableSession = async (tableId, preferredCheckCode = '') => {
    if (!tableId) {
      setActiveTableSession(null); setTableSessionChecks([]); setSelectedCheckId('');
      setLinkedTableInfo(null); return;
    }
    try {
      const res = await fetch(
        `/api/table-sessions/active?table_id=${tableId}&outlet_id=${scope.outlet_id || ''}`,
        { headers: scopeHeaders() }
      );
      const data = await res.json().catch(() => ({}));
      const session = data?.session || null;
      const checks = Array.isArray(data?.checks) ? data.checks : [];
      setActiveTableSession(session);
      // Reflect the session's PAX in the order form so re-orders on an already
      // occupied table carry the correct guest_count instead of resetting to 1 (Bug 8725).
      if (session && session.pax != null && Number(session.pax) > 0) setGuestCount(Number(session.pax));
      setTableSessionChecks(checks);
      setLinkedTableInfo(data?.link_info || null);
      setShowLinkPicker(false);
      setBlockingCheckCodes([]);
      if (session) setTableCollapsed(true); // auto-collapse when session is active
      if (preferredCheckCode) {
        const match = checks.find(c => c.check_code === preferredCheckCode);
        if (match) { setSelectedCheckId(match.id); return; }
      }
      if (checks.length > 0) setSelectedCheckId(checks[0].id);
      else setSelectedCheckId('');
    } catch { /* silent */ }
  };

  const openSessionForSelectedTable = async () => {
    if (!selectedTableId) return;
    setSessionBusy(true);
    try {
      const res = await fetch('/api/table-sessions/open', {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ table_id: selectedTableId, outlet_id: scope.outlet_id || null })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to open session');
      setActiveTableSession(data.session);
      setTableSessionChecks(data.checks || []);
      setSelectedCheckId(data.checks?.[0]?.id || '');
      setLinkedTableInfo(null);
      setBlockingCheckCodes([]);
      loadAllTableSessions();
    } catch { setError('Network error'); }
    finally { setSessionBusy(false); }
  };

  const closeSessionForSelectedTable = async () => {
    if (!activeTableSession?.id) return false;
    const confirmed = window.confirm('Close session for this table? All checks must be settled first.');
    if (!confirmed) return false;
    setSessionBusy(true);
    try {
      const res = await fetch(`/api/table-sessions/${activeTableSession.id}/close`, {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.blocking_checks) setBlockingCheckCodes(data.blocking_checks);
        setError(data.error || 'Cannot close session — unsettled checks exist');
        return false;
      }
      setActiveTableSession(null); setTableSessionChecks([]); setSelectedCheckId('');
      setLinkedTableInfo(null); setBlockingCheckCodes([]);
      loadAllTableSessions();
      return true;
    } catch { setError('Network error'); return false; }
    finally { setSessionBusy(false); }
  };

  // Clear Session button on the Menu & Order screen itself — reachable even when
  // a table's session has zero orders (the "stuck occupied table" case), which the
  // step-flow's own bill/transfer buttons don't cover since those only render when
  // existingOrders.length > 0.
  const handleClearSessionFromMenu = async () => {
    const closed = await closeSessionForSelectedTable();
    if (closed) setStep('table');
  };

  const createNewCheck = async () => {
    if (!activeTableSession?.id) return;
    const nextCode = `C${tableSessionChecks.length + 1}`;
    setSessionBusy(true);
    try {
      const res = await fetch(`/api/table-sessions/${activeTableSession.id}/checks`, {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ check_code: nextCode })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to create check');
      await loadTableSession(selectedTableId, nextCode);
    } catch { setError('Network error'); }
    finally { setSessionBusy(false); }
  };

  const linkTable = async (targetTableId) => {
    if (!activeTableSession?.id) return;
    setLinkBusy(true);
    try {
      const res = await fetch(`/api/table-sessions/${activeTableSession.id}/link`, {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_table_id: targetTableId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to link table');
      setShowLinkPicker(false);
      await loadTableSession(selectedTableId);
      loadAllTableSessions();
    } catch { setError('Network error'); }
    finally { setLinkBusy(false); }
  };

  const unlinkTable = async (secondarySessionId) => {
    if (!activeTableSession?.id) return;
    setLinkBusy(true);
    try {
      const res = await fetch(`/api/table-sessions/${activeTableSession.id}/unlink-table`, {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ secondary_session_id: secondarySessionId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to unlink table');
      await loadTableSession(selectedTableId);
      loadAllTableSessions();
    } catch { setError('Network error'); }
    finally { setLinkBusy(false); }
  };

  const openLinkedOrders = async (primarySessionId) => {
    try {
      const res = await fetch(`/api/table-sessions/${primarySessionId}/linked-orders`, { headers: scopeHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load linked orders');
      setLinkedOrdersData({ ...data, primary_session_id: primarySessionId });
    } catch { setError('Network error loading linked orders'); }
  };

  // ── Status badge helper ───────────────────────────────────────────────────
  const statusBadge = (status) => {
    const s = String(status || '').toUpperCase();
    const map = {
      OPEN: { bg: '#eff6ff', color: '#1d4ed8', label: 'Open' },
      PARTIAL_KOT: { bg: '#fef3c7', color: '#92400e', label: 'Partial KOT' },
      KOT_SENT: { bg: '#fef3c7', color: '#b45309', label: 'KOT Sent' },
      PREPARING: { bg: '#e0f2fe', color: '#0369a1', label: 'Preparing' },
      READY: { bg: '#dcfce7', color: '#15803d', label: 'Ready' },
      CLOSED: { bg: 'var(--theme-border-light)', color: '#475569', label: 'Closed' },
      SETTLED: { bg: '#dcfce7', color: '#15803d', label: 'Settled' },
      VOID: { bg: '#fee2e2', color: '#b91c1c', label: 'Void' },
      VOIDED: { bg: '#fee2e2', color: '#b91c1c', label: 'Voided' },
      RAISED: { bg: '#fef3c7', color: '#92400e', label: 'Raised' },
      SERVED: { bg: '#f0fdf4', color: '#166534', label: 'Served' },
      CANCELLED: { bg: '#fee2e2', color: '#b91c1c', label: 'Cancelled' }
    };
    const cfg = map[s] || { bg: 'var(--theme-border-light)', color: C.muted, label: s };
    return <span style={{ padding: '0.2rem 0.6rem', borderRadius: '999px', background: cfg.bg, color: cfg.color, fontSize: '0.82rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{cfg.label}</span>;
  };

  const kotActionColour = (action) => ({ start: C.accent, ready: C.warn, served: C.success }[action] || C.primary);
  const orderTypeLabel = (code) => ({ DINE_IN: 'Dine-In', ROOM_SERVICE: 'Room Svc', TAKE_AWAY: 'Take Away', TAKEAWAY: 'Take Away', IN_ROOM_DINING: 'Dine-In', DELIVERY: 'Delivery' }[String(code || '').toUpperCase()] || code || '—');
  const orderTypePill = (code) => {
    const cfg = {
      DINE_IN:      { label: 'Dine-In',   bg: '#ede9fe', color: '#6d28d9' },
      ROOM_SERVICE: { label: 'Room Svc',  bg: '#dbeafe', color: '#1d4ed8' },
      TAKE_AWAY:    { label: 'Take Away', bg: '#fef3c7', color: '#92400e' },
      TAKEAWAY:     { label: 'Take Away', bg: '#fef3c7', color: '#92400e' },
      IN_ROOM_DINING:{ label: 'Dine-In',  bg: '#ede9fe', color: '#6d28d9' },
      DELIVERY:     { label: 'Delivery',  bg: '#d1fae5', color: '#065f46' },
    }[String(code || '').toUpperCase()] || { label: code || '—', bg: '#f1f5f9', color: '#475569' };
    return <span style={{ padding: '0.15rem 0.5rem', borderRadius: '999px', background: cfg.bg, color: cfg.color, fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{cfg.label}</span>;
  };

  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: 'system-ui, sans-serif' }}>

      {/* Breadcrumb is rendered by the global app shell (App.js breadcrumbParts).
          The local "KOT & POS / Orders" strip used to be here but duplicated the
          global one — removed to avoid the double-breadcrumb visual. */}

      {error && <div className="error-banner" style={{ margin: '0.75rem 1.5rem 0' }}>{error} <button onClick={() => setError('')} style={{ float: 'right', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 700, color: C.danger }}>✕</button></div>}
      {message && <div className="success-banner" style={{ margin: '0.75rem 1.5rem 0' }}>{message} <button onClick={() => setMessage('')} style={{ float: 'right', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 700, color: '#15803d' }}>✕</button></div>}

      {/* Persistent breadcrumb-style link — lets staff jump straight back to
          the KOT & POS — Orders dashboard from any step in the order-
          creation flow (service-type / table / room / menu). The per-
          screen "Back" button still goes one step back, giving both
          patterns. */}
      {step && (
        <div style={{ padding: '0.5rem 1rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <span onClick={() => setStep(null)}
            style={{ cursor: 'pointer', color: '#1e40af', fontSize: '0.85rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
            &larr; KOT &amp; POS &mdash; Orders
          </span>
        </div>
      )}

      {/* ── Step: Service Type Selection ── */}
      {step === 'service-type' && (
        <div>
          <ServiceTypeSelector onSelect={handleServiceTypeSelect} allowedTypes={selectedOutlet?.kot_order_types} />
        </div>
      )}

      {/* ── Step: Table Selection ── */}
      {step === 'table' && (
        <TableSelector
          tables={tables}
          allTableSessions={allTableSessions}
          recentOrders={recentOrders}
          onSelect={handleTableSelect}
          onBack={() => setStep('service-type')}
          isMobile={isMobile}
          onGenerateTableBill={handleGenerateTableBill}
          onPrintKot={handlePrintKotFromCard}
        />
      )}

      {/* ── Step: Room Selection ── */}
      {step === 'room' && (
        <RoomSelector
          rooms={occupiedRooms}
          roomOrders={roomServiceOrders}
          onSelect={handleRoomSelect}
          onBack={() => setStep('service-type')}
          isMobile={isMobile}
        />
      )}

      {/* ── Link Table bar (restaurant/dine-in only, shown above menu) ── */}
      {step === 'menu' && selectedServiceType === 'restaurant' && selectedTableId && (() => {
        const isPrimary = linkedTableInfo?.is_primary === true;
        const isSecondary = linkedTableInfo?.is_secondary === true;
        const linkedTables = linkedTableInfo?.linked_tables || [];
        const canLink = activeTableSession?.id && !isSecondary;
        const linkableTables = tables.filter(t =>
          String(t.id) !== String(selectedTableId) &&
          allTableSessions.some(s => String(s.table_id) === String(t.id) || String(s.kot_table_id) === String(t.id)) &&
          !linkedTables.some(lt => String(lt.table_id) === String(t.id) || String(lt.kot_table_id) === String(t.id))
        );
        if (!canLink && !isPrimary && !isSecondary) return null;
        return (
          <div style={{ padding: '0.45rem 1rem', background: '#f0f7ff', borderBottom: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            {(isPrimary || isSecondary) && (
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1d4ed8' }}>
                🔗 {isPrimary ? `Linked with: ${linkedTables.map(lt => lt.table_name).join(', ')}` : `Linked to: ${linkedTableInfo?.primary_table?.table_name}`}
              </span>
            )}
            {isPrimary && linkedTables.map(lt => (
              <button key={lt.session_id} type="button" onClick={() => unlinkTable(lt.session_id)} disabled={linkBusy}
                style={{ fontSize: '0.72rem', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                ✕ Unlink {lt.table_name}
              </button>
            ))}
            {isPrimary && (
              <button type="button" onClick={() => openLinkedOrders(activeTableSession.id)}
                style={{ fontSize: '0.72rem', color: '#fff', background: '#1d4ed8', border: 'none', borderRadius: '999px', padding: '0.2rem 0.65rem', cursor: 'pointer', fontWeight: 700, marginLeft: 'auto' }}>
                View Combined
              </button>
            )}
            {canLink && !showLinkPicker && (
              <button type="button" onClick={() => setShowLinkPicker(true)} disabled={linkBusy}
                style={{ fontSize: '0.78rem', color: '#1d4ed8', background: '#fff', border: '1.5px solid #93c5fd', borderRadius: '999px', padding: '0.2rem 0.75rem', cursor: 'pointer', fontWeight: 700, marginLeft: isPrimary || isSecondary ? '0' : 'auto' }}>
                🔗 Link Table
              </button>
            )}
            {showLinkPicker && (
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center', marginLeft: 'auto' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Link to:</span>
                {linkableTables.length === 0
                  ? <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>No open sessions available</span>
                  : linkableTables.map(t => (
                    <button key={t.id} type="button" onClick={() => linkTable(t.id)} disabled={linkBusy}
                      style={{ padding: '0.2rem 0.65rem', border: '1.5px solid #1d4ed8', borderRadius: '999px', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700 }}>
                      {t.table_no}
                    </button>
                  ))
                }
                <button type="button" onClick={() => setShowLinkPicker(false)}
                  style={{ padding: '0.2rem 0.55rem', border: '1px solid #e5e7eb', borderRadius: '999px', background: '#fff', color: '#94a3b8', cursor: 'pointer', fontSize: '0.75rem' }}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Step: Menu & Order ── */}
      {step === 'menu' && (
        <MenuAndOrder
          menuItems={menuItems}
          cartItems={cartItems}
          itemQuantities={itemQuantities}
          tableInfo={selectedTableObj}
          roomInfo={selectedRoomObj}
          serviceType={selectedServiceType}
          onAddToCart={addToCart}
          onUpdateCartQty={updateCartQty}
          onRemoveFromCart={removeFromCart}
          onUpdateNote={updateCartInstructions}
          onUpdatePreference={updateCartPreference}
          onUpdateCardQty={updateCardQty}
          onPlaceOrder={handleMenuPlaceOrder}
          onBack={handleMenuBack}
          isMobile={isMobile}
          placing={placingOrder}
          cartSubtotal={subtotal}
          cartTotal={Math.max(0, subtotal - cartDiscountAmount)}
          discountAmount={cartDiscountAmount}
          discountType={cartDiscountType}
          discountValue={cartDiscountValue}
          onDiscountTypeChange={setCartDiscountType}
          onDiscountValueChange={setCartDiscountValue}
          onClearCart={() => { setCartItems([]); setCartDiscountValue(''); }}
          onSaveOrder={handleSaveOrderDraft}
          savingOrder={savingOrderDraft}
          onPrintLatestKot={handlePrintLatestKotForTable}
          onGuestDetails={selectedServiceType === 'restaurant' ? handleMenuGuestDetails : null}
          existingOrders={recentOrders.filter(o => {
            if (!selectedTableId) return false;
            if (String(o.table_id) !== String(selectedTableId)) return false;
            if (!['OPEN', 'PARTIAL_KOT', 'KOT_SENT', 'PREPARING', 'READY'].includes(o.status)) return false;
            // Only show orders from today's business date — stale open orders from previous days are excluded
            const today = new Date().toISOString().slice(0, 10);
            const orderDate = (o.business_date || o.opened_at || '').toString().slice(0, 10);
            return orderDate === today;
          })}
          sessionInfo={activeTableSession}
          fetchOrderItems={async (orderId) => {
            const res = await fetch(`/api/v1/kot/orders/${orderId}/items`, { headers: scopeHeaders() });
            if (!res.ok) return [];
            return await res.json();
          }}
          tables={tables}
          onGenerateBill={selectedServiceType === 'restaurant' ? handleMenuGenerateBill : null}
          onTableTransfer={selectedServiceType === 'restaurant' ? handleMenuTableTransfer : null}
          onExit={selectedServiceType === 'restaurant' ? () => setStep('table') : null}
          onClearSession={selectedServiceType === 'restaurant' ? handleClearSessionFromMenu : null}
        />
      )}

      {/* ── Order History (shown when no step is active or when in step flow — hidden) ── */}
      {!step && (
      <div style={{ padding: isMobile ? '0.75rem' : '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h2 style={{ margin: 0, color: C.primary, fontWeight: 800, fontSize: '1.3rem' }}>KOT & POS — Orders</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button onClick={() => setStep('service-type')} style={{ ...tapMin, padding: '0.45rem 0.9rem', border: 'none', borderRadius: '8px', background: C.primary, color: '#fff', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>+ New Order</button>
            <button onClick={refreshAll} disabled={refreshing} className="btn-muted" style={{ ...tapMin, padding: '0.45rem 0.9rem', border: 'none', borderRadius: '8px', cursor: refreshing ? 'not-allowed' : 'pointer', opacity: refreshing ? 0.6 : 1, fontSize: '0.82rem', fontWeight: 600 }}>{refreshing ? '↻ Refreshing…' : '↻ Refresh'}</button>
          </div>
        </div>
      </div>
      )}

      {/* OLD two-column order creation — hidden (replaced by step flow) */}
      {false && (
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 420px', gap: '1rem', alignItems: 'start' }}>

        {/* ════════════════ LEFT: Order Creation ════════════════ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

          {/* ── Order Setup (collapsible) ── */}
          <div style={card}>
            <div style={collapsibleHead} onClick={() => setOrderSetupCollapsed(!orderSetupCollapsed)}>
              <span>Order Setup {chevron(orderSetupCollapsed)}</span>
            </div>
            {orderSetupCollapsed ? (
              collapsedSummary(`${orderTypeLabel(selectedOrderType)} \u2022 ${stationNameById(selectedStationId)} \u2022 ${guestCount} guest(s)`)
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.6rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: C.muted, marginBottom: '0.25rem' }}>Order Type</div>
                  <select value={selectedOrderType} onChange={(e) => setSelectedOrderType(e.target.value)} style={inputStyle}>
                    {(orderTypes.length ? orderTypes : [{ order_type_code: 'DINE_IN', order_type_name: 'DINE IN' }]).map((ot) => (
                      <option key={ot.order_type_code} value={ot.order_type_code}>{ot.order_type_name || ot.order_type_code}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: C.muted, marginBottom: '0.25rem' }}>Kitchen Station</div>
                  <select value={selectedStationId} onChange={(e) => setSelectedStationId(e.target.value)} style={{ ...inputStyle, borderColor: !selectedStationId ? C.warn : C.border }}>
                    <option value="">Select Station</option>
                    {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: C.muted, marginBottom: '0.25rem' }}>Guests</div>
                  <input type="number" min="1" value={guestCount} onChange={(e) => setGuestCount(e.target.value)} placeholder="Count" style={inputStyle} />
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: C.muted, marginBottom: '0.25rem' }}>Tax Profile</div>
                  <select value={selectedTaxProfileId} onChange={(e) => setSelectedTaxProfileId(e.target.value)} style={inputStyle}>
                    <option value="">Outlet Default</option>
                    {taxProfiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: C.muted, marginBottom: '0.25rem' }}>Pricing Profile</div>
                  <select value={selectedPricingProfileId} onChange={(e) => setSelectedPricingProfileId(e.target.value)} style={inputStyle}>
                    <option value="">Outlet Default</option>
                    {pricingProfiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* ── Room/Guest lookup (conditional, collapsible) ── */}
          {String(selectedOrderType || '').toUpperCase() === 'ROOM_SERVICE' && propertyConfig?.module_front_office_enabled !== false && (
            <div style={card}>
              <div style={collapsibleHead} onClick={() => setGuestInfoCollapsed(!guestInfoCollapsed)}>
                <span>Guest Information {chevron(guestInfoCollapsed)}</span>
                {String(selectedOrderType || '').toUpperCase() === 'ROOM_SERVICE' && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', fontWeight: 600, color: C.text, cursor: 'pointer', textTransform: 'none', letterSpacing: 0 }}
                    onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={isExternalGuest === true} onChange={(e) => { const next = e.target.checked; setIsExternalGuest(next); if (next) { setRoomGuestName(''); setRoomGuestMeta(null); setGuestInfoCollapsed(true); } else { setGuestInfoCollapsed(false); } }} />
                    External guest
                  </label>
                )}
              </div>
              {guestInfoCollapsed ? (
                collapsedSummary(isExternalGuest ? 'External guest (no room required)' : roomGuestMeta ? `\u2713 Room ${roomNumber} \u2014 ${roomGuestName}` : roomNumber ? `Room ${roomNumber}` : 'Click to enter room details')
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto 1fr', gap: '0.6rem', alignItems: 'end' }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: C.muted, marginBottom: '0.25rem' }}>Room Number{isExternalGuest && String(selectedOrderType || '').toUpperCase() === 'ROOM_SERVICE' ? ' (Optional)' : ''}</div>
                      <input value={roomNumber} onChange={(e) => { setRoomNumber(e.target.value); setRoomGuestName(''); setRoomGuestMeta(null); }} placeholder="e.g. 501" style={inputStyle} />
                    </div>
                    <button type="button" onClick={lookupInhouseGuest} disabled={roomGuestLoading}
                      style={{ ...btnStyle, background: C.accent, whiteSpace: 'nowrap', alignSelf: 'flex-end' }}>
                      {roomGuestLoading ? '...' : 'Lookup'}
                    </button>
                    <div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: C.muted, marginBottom: '0.25rem' }}>Guest Name</div>
                      <input value={roomGuestName} readOnly placeholder="Auto from room" style={{ ...inputStyle, background: 'var(--theme-bg-light)', color: roomGuestName ? C.success : C.muted }} />
                    </div>
                  </div>
                  {roomGuestMeta && <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: C.success, fontWeight: 600 }}>{'\u2713'} In-house guest verified</div>}
                </>
              )}
            </div>
          )}

          {/* ── Table & Session (conditional, collapsible) ── */}
          {String(selectedOrderType || '').toUpperCase() === 'ROOM_SERVICE' && (
            <div style={{ ...card, borderLeft: activeTableSession ? `4px solid ${C.success}` : undefined }}>
              <div style={collapsibleHead} onClick={() => setTableCollapsed(!tableCollapsed)}>
                <span>Table & Session {chevron(tableCollapsed)}</span>
                <span style={{ fontSize: '0.72rem', color: C.muted, textTransform: 'none', letterSpacing: 0 }}>{filteredTables.length}/{tables.length} tables</span>
              </div>
              {tableCollapsed ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {collapsedSummary(
                    selectedTableId
                      ? `${tables.find(t => String(t.id) === String(selectedTableId))?.table_no || 'Table'} \u2022 ${activeTableSession ? `Session #${activeTableSession.session_no} \u2022 \u25CF OPEN` : '\u25CB No Session'}`
                      : 'No table selected'
                  )}
                  {selectedTableId && (
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.3rem' }}>
                      {!activeTableSession && (
                        <button type="button" onClick={openSessionForSelectedTable} disabled={sessionBusy}
                          style={{ ...tapMin, padding: '0.25rem 0.65rem', border: 'none', borderRadius: '6px', background: C.primary, color: '#fff', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                          {sessionBusy ? '...' : 'Open Session'}
                        </button>
                      )}
                      {activeTableSession && (
                        <>
                          <button type="button" onClick={createNewCheck} disabled={sessionBusy}
                            style={{ ...tapMin, padding: '0.25rem 0.65rem', border: `1px solid ${C.success}`, borderRadius: '6px', background: '#f0fdf4', color: '#15803d', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                            + Check
                          </button>
                          <button type="button" onClick={closeSessionForSelectedTable} disabled={sessionBusy}
                            style={{ ...tapMin, padding: '0.25rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '6px', background: 'var(--theme-bg-light)', color: C.muted, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                            {sessionBusy ? '...' : 'Close'}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <input value={tableSearchTerm} onChange={(e) => setTableSearchTerm(e.target.value)} placeholder="Search table / zone\u2026" style={{ ...inputStyle, marginBottom: '0.5rem' }} />
                  {!tables.length ? (
                    <div style={{ color: C.muted, fontSize: '0.85rem', padding: '0.5rem 0' }}>No tables configured for this outlet.</div>
                  ) : (
                    <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.45rem' }}>
                      {filteredTables.map((tbl) => {
                        const tableId = String(tbl.id);
                        const stat = tableOccupancy[tableId];
                        const liveSession = allTableSessions.find(s => String(s.table_id) === tableId || String(s.kot_table_id) === tableId);
                        const occupied = Boolean(liveSession) || Boolean(stat?.occupied);
                        const isSelected = selectedTableId === tableId;
                        return (
                          <button key={tbl.id} type="button"
                            onClick={() => { setSelectedTableId(tableId); loadTableSession(tableId); }}
                            style={{ textAlign: 'center', border: isSelected ? `2px solid ${C.accent}` : `1px solid ${C.border}`, borderRadius: '10px', padding: '0.5rem 0.4rem', background: isSelected ? '#eff6ff' : occupied ? '#fff7ed' : '#fff', cursor: 'pointer', transition: 'all 0.15s' }}>
                            <div style={{ fontWeight: 700, fontSize: '0.92rem', color: isSelected ? C.accent : C.text }}>{tbl.table_no}</div>
                            {tbl.zone && <div style={{ fontSize: '0.75rem', color: C.muted }}>{tbl.zone}</div>}
                            <div style={{ marginTop: '0.2rem', fontSize: '0.75rem', fontWeight: 700, color: occupied ? C.warn : C.success }}>
                              {occupied ? '\u25CF Occupied' : '\u25CB Free'}
                            </div>
                            {liveSession && <div style={{ fontSize: '0.72rem', color: C.muted }}>#{liveSession.session_no}</div>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Session Control Panel (inline, shown when table selected and NOT collapsed) ── */}
          {selectedTableId && !tableCollapsed && (() => {
            const isPrimary = linkedTableInfo?.is_primary === true;
            const isSecondary = linkedTableInfo?.is_secondary === true;
            const linkedTables = linkedTableInfo?.linked_tables || [];
            const canLink = activeTableSession?.id && !isSecondary;
            const linkableTables = tables.filter(t =>
              String(t.id) !== String(selectedTableId) &&
              allTableSessions.some(s => String(s.table_id) === String(t.id) || String(s.kot_table_id) === String(t.id)) &&
              !linkedTables.some(lt => String(lt.table_id) === String(t.id) || String(lt.kot_table_id) === String(t.id))
            );
            const tableName = tables.find(t => String(t.id) === String(selectedTableId))?.table_no || 'Table';
            return (
              <div style={{ ...card, borderLeft: `4px solid ${activeTableSession ? C.success : C.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, color: C.primary }}>{tableName}</span>
                  {activeTableSession
                    ? <span style={{ padding: '0.15rem 0.6rem', borderRadius: '999px', background: '#dcfce7', color: '#15803d', fontSize: '0.72rem', fontWeight: 700 }}>● OPEN · Session #{activeTableSession.session_no}</span>
                    : <span style={{ padding: '0.15rem 0.6rem', borderRadius: '999px', background: 'var(--theme-border-light)', color: C.muted, fontSize: '0.72rem', fontWeight: 700 }}>○ No Active Session</span>
                  }
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.35rem' }}>
                    {!activeTableSession && (
                      <button type="button" onClick={openSessionForSelectedTable} disabled={sessionBusy}
                        style={{ ...tapMin, padding: '0.3rem 0.75rem', border: 'none', borderRadius: '6px', background: C.primary, color: '#fff', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                        {sessionBusy ? '...' : 'Open Session'}
                      </button>
                    )}
                    {activeTableSession && (
                      <>
                        <button type="button" onClick={createNewCheck} disabled={sessionBusy}
                          style={{ ...tapMin, padding: '0.3rem 0.75rem', border: `1px solid ${C.success}`, borderRadius: '6px', background: '#f0fdf4', color: '#15803d', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                          + Check
                        </button>
                        <button type="button" onClick={closeSessionForSelectedTable} disabled={sessionBusy}
                          style={{ ...tapMin, padding: '0.3rem 0.75rem', border: `1px solid ${C.border}`, borderRadius: '6px', background: 'var(--theme-bg-light)', color: C.muted, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                          {sessionBusy ? '...' : 'Close Session'}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {blockingCheckCodes.length > 0 && (
                  <div style={{ fontSize: '0.75rem', color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '0.35rem 0.6rem', marginBottom: '0.5rem' }}>
                    Unsettled checks: {blockingCheckCodes.join(', ')} — settle before closing.
                  </div>
                )}

                {/* Check tabs */}
                {tableSessionChecks.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    {tableSessionChecks.map(check => (
                      <button key={check.id} type="button" onClick={() => setSelectedCheckId(check.id)}
                        style={{ padding: '0.25rem 0.7rem', border: selectedCheckId === check.id ? `2px solid ${C.accent}` : `1px solid ${C.border}`, borderRadius: '999px', background: selectedCheckId === check.id ? '#eff6ff' : 'var(--theme-bg-light)', color: selectedCheckId === check.id ? C.accent : C.muted, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}>
                        {check.check_code}{Number(check.total_amount || 0) > 0 && <span style={{ opacity: 0.7, marginLeft: '0.3rem' }}>₹{Number(check.total_amount).toFixed(0)}</span>}
                      </button>
                    ))}
                  </div>
                )}

                {/* Linked table info */}
                {(isPrimary || isSecondary) && (
                  <div style={{ background: '#eff6ff', border: `1px solid #bfdbfe`, borderRadius: '8px', padding: '0.5rem 0.75rem', marginBottom: '0.4rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1d4ed8' }}>🔗 Linked Bill</span>
                      {isPrimary && linkedTables.map(lt => (
                        <span key={lt.session_id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', background: '#dbeafe', color: '#1e40af', borderRadius: '999px', padding: '0.15rem 0.5rem', fontSize: '0.72rem', fontWeight: 600 }}>
                          {lt.table_name}
                          <button type="button" onClick={() => unlinkTable(lt.session_id)} disabled={linkBusy}
                            style={{ border: 'none', background: 'none', color: C.danger, cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem', padding: '0 0.1rem', lineHeight: 1 }}>×</button>
                        </span>
                      ))}
                      {isSecondary && linkedTableInfo?.primary_table && (
                        <span style={{ fontSize: '0.72rem', color: C.muted }}>Primary: <b>{linkedTableInfo.primary_table.table_name}</b></span>
                      )}
                      {isPrimary && (
                        <button type="button" onClick={() => openLinkedOrders(activeTableSession.id)}
                          style={{ marginLeft: 'auto', padding: '0.2rem 0.65rem', border: 'none', borderRadius: '999px', background: '#1d4ed8', color: '#fff', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700 }}>
                          View Combined
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Link picker */}
                {showLinkPicker && (
                  <div style={{ background: 'var(--theme-bg-light)', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '0.5rem 0.75rem', marginBottom: '0.4rem' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: C.muted, marginBottom: '0.4rem' }}>Select table to link:</div>
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                      {linkableTables.length === 0
                        ? <span style={{ fontSize: '0.75rem', color: 'var(--theme-text-muted)' }}>No open sessions to link</span>
                        : linkableTables.map(t => (
                            <button key={t.id} type="button" onClick={() => linkTable(t.id)} disabled={linkBusy}
                              style={{ padding: '0.25rem 0.7rem', border: `1.5px solid ${C.primary}`, borderRadius: '999px', background: '#eff6ff', color: C.primary, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                              {t.table_no}
                            </button>
                          ))
                      }
                      <button type="button" onClick={() => setShowLinkPicker(false)}
                        style={{ padding: '0.25rem 0.7rem', border: `1px solid ${C.border}`, borderRadius: '999px', background: 'var(--theme-bg-light)', color: C.muted, cursor: 'pointer', fontSize: '0.78rem' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {canLink && !showLinkPicker && (
                  <button type="button" onClick={() => setShowLinkPicker(true)} disabled={linkBusy}
                    style={{ padding: '0.25rem 0.7rem', border: `1.5px solid ${C.primary}`, borderRadius: '999px', background: '#fff', color: C.primary, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                    🔗 Link Table
                  </button>
                )}
              </div>
            );
          })()}

          {/* ── Menu Items ── */}
          <div style={card}>
            <div style={secHead}>Menu</div>
            {/* Search */}
            <input placeholder="Search item by name, SKU or category…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              style={{ ...inputStyle, marginBottom: '0.6rem' }} />
            {/* Category filter pills */}
            {menuCategories.length > 1 && (
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.65rem' }}>
                {menuCategories.map(cat => (
                  <button key={cat} type="button" onClick={() => setSelectedCategory(cat)}
                    style={{ padding: '0.2rem 0.65rem', borderRadius: '999px', border: `1.5px solid ${selectedCategory === cat ? C.accent : C.border}`, background: selectedCategory === cat ? C.accent : '#fff', color: selectedCategory === cat ? '#fff' : C.muted, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.15s' }}>
                    {cat}
                  </button>
                ))}
              </div>
            )}
            {/* Item grid */}
            <div style={{ maxHeight: '360px', overflowY: 'auto', marginBottom: '0.5rem' }}>
              {filteredMenuItems.length === 0 ? (
                <div style={{ color: C.muted, padding: '1rem', textAlign: 'center', fontSize: '0.85rem' }}>No items found</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(130px, 1fr))' : 'repeat(auto-fill, minmax(155px, 1fr))', gap: '0.55rem' }}>
                  {filteredMenuItems.map((item) => {
                    const inCart = cartItems.find(ci => ci.menu_item_id === item.id);
                    return (
                      <div key={item.id}
                        style={{ border: `1.5px solid ${inCart ? C.accent : C.border}`, borderRadius: '10px', overflow: 'hidden', background: inCart ? '#eff6ff' : '#fff', display: 'flex', flexDirection: 'column', transition: 'border-color 0.15s' }}>
                        {item.image_url && (
                          <div style={{ width: '100%', aspectRatio: '1/1', overflow: 'hidden', background: '#f5f5f5' }}>
                            <img src={item.image_url} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          </div>
                        )}
                        <div style={{ padding: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: C.text, lineHeight: 1.25, minHeight: '2.2em' }}>{item.name}</div>
                        {item.category_name && <div style={{ fontSize: '0.68rem', color: C.muted, background: 'var(--theme-border-light)', borderRadius: '4px', padding: '0.1rem 0.35rem', alignSelf: 'flex-start' }}>{item.category_name}</div>}
                        <div style={{ fontWeight: 800, fontSize: '0.95rem', color: C.primary }}>₹{Number(item.effective_price || item.base_price || 0).toFixed(2)}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.25rem', alignItems: 'center' }}>
                          <button type="button" onClick={() => updateCardQty(item.id, -1)}
                            style={{ ...tapMin, padding: '0.25rem 0', border: 'none', borderRadius: '6px', background: 'var(--theme-border)', color: C.text, cursor: 'pointer', fontWeight: 700, fontSize: '1rem' }}>−</button>
                          <span style={{ textAlign: 'center', fontWeight: 700, color: C.text, fontSize: '0.9rem' }}>{itemQuantities[item.id] || 1}</span>
                          <button type="button" onClick={() => updateCardQty(item.id, 1)}
                            style={{ ...tapMin, padding: '0.25rem 0', border: 'none', borderRadius: '6px', background: '#dcfce7', color: '#15803d', cursor: 'pointer', fontWeight: 700, fontSize: '1rem' }}>+</button>
                        </div>
                        <button type="button" onClick={() => addToCart(item)}
                          style={{ ...tapMin, padding: '0.3rem', border: 'none', borderRadius: '6px', background: inCart ? C.accent : C.primary, color: '#fff', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                          {inCart ? '✓ In Cart' : 'Add'}
                        </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* ════════════════ RIGHT: Cart ════════════════ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

          {/* ── Cart ── */}
          <div style={{ ...card, borderTop: `4px solid ${C.accent}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
              <div style={{ ...secHead, marginBottom: 0, borderBottom: 'none' }}>Cart {cartItems.length > 0 && <span style={{ background: C.accent, color: '#fff', borderRadius: '999px', padding: '0.1rem 0.5rem', fontSize: '0.72rem', marginLeft: '0.4rem' }}>{cartItems.length}</span>}</div>
              {cartItems.length > 0 && (
                <button type="button" onClick={clearTableCart} style={{ border: 'none', background: 'none', color: C.danger, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>Clear all</button>
              )}
            </div>
            {cartItems.length === 0 ? (
              <div style={{ color: C.muted, fontSize: '0.85rem', textAlign: 'center', padding: '1.5rem 0' }}>Cart is empty — add items from the menu above</div>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.75rem', maxHeight: '400px', overflowY: 'auto' }}>
                  {cartItems.map((ci) => (
                    <div key={ci.menu_item_id} style={{ padding: '0.5rem 0.6rem', background: 'var(--theme-bg-light)', borderRadius: '8px', border: `1px solid ${C.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {ci.image_url && <img src={ci.image_url} alt="" style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '5px', flexShrink: 0 }} />}
                        <div style={{ flex: 1, fontWeight: 600, fontSize: '0.85rem', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ci.is_out_of_menu && <span style={{ fontSize: '0.65rem', background: '#fbbf24', color: '#78350f', borderRadius: '3px', padding: '0.05rem 0.3rem', marginRight: '0.3rem', fontWeight: 700, verticalAlign: 'middle' }}>OOM</span>}
                          {ci.menu_item_name}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                          <button onClick={() => updateCartQty(ci.menu_item_id, -1)} style={{ ...tapMin, width: '26px', height: '26px', padding: 0, border: 'none', borderRadius: '6px', background: 'var(--theme-border)', color: C.text, cursor: 'pointer', fontWeight: 700 }}>−</button>
                          <span style={{ minWidth: '20px', textAlign: 'center', fontWeight: 700, fontSize: '0.85rem' }}>{ci.qty}</span>
                          <button onClick={() => updateCartQty(ci.menu_item_id, 1)} style={{ ...tapMin, width: '26px', height: '26px', padding: 0, border: 'none', borderRadius: '6px', background: '#dcfce7', color: '#15803d', cursor: 'pointer', fontWeight: 700 }}>+</button>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: C.primary, minWidth: '60px', textAlign: 'right' }}>₹{(ci.qty * ci.unit_price).toFixed(2)}</div>
                        <button onClick={() => removeFromCart(ci.menu_item_id)} style={{ border: 'none', background: 'none', color: 'var(--theme-text-muted)', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: '0 0.1rem' }}>✕</button>
                      </div>
                      <input
                        value={ci.special_instructions || ''}
                        onChange={(e) => updateCartInstructions(ci.menu_item_id, e.target.value)}
                        placeholder="Special instructions..."
                        style={{ width: '100%', marginTop: '0.3rem', padding: '0.25rem 0.5rem', border: `1px solid ${C.border}`, borderRadius: '5px', fontSize: '0.75rem', boxSizing: 'border-box', color: '#475569' }}
                      />
                    </div>
                  ))}
                </div>
                {/* Subtotal bar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0.75rem', background: '#eff6ff', borderRadius: '8px', marginBottom: '0.75rem' }}>
                  <span style={{ fontWeight: 600, color: C.muted, fontSize: '0.88rem' }}>Subtotal ({cartItems.length} items)</span>
                  <span style={{ fontWeight: 800, color: C.primary, fontSize: '1.05rem' }}>₹{subtotal.toFixed(2)}</span>
                </div>
              </>
            )}
            {/* Out-of-Menu Item */}
            <div style={{ padding: '0.6rem', background: '#fef3c7', borderRadius: '8px', marginBottom: '0.6rem', border: '1px solid #fbbf24' }}>
              <div style={{ fontWeight: 600, fontSize: '0.78rem', color: '#92400e', marginBottom: '0.4rem' }}>Out of Menu Item</div>
              <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                <input value={oomName} onChange={e => setOomName(e.target.value)} placeholder="Item name"
                  style={{ flex: 2, minWidth: '100px', padding: '0.35rem 0.5rem', border: `1px solid ${C.border}`, borderRadius: '5px', fontSize: '0.82rem', boxSizing: 'border-box' }} />
                <input type="number" value={oomPrice} onChange={e => setOomPrice(e.target.value)} placeholder="Price"
                  style={{ flex: 1, minWidth: '60px', padding: '0.35rem 0.5rem', border: `1px solid ${C.border}`, borderRadius: '5px', fontSize: '0.82rem', boxSizing: 'border-box' }} />
                <input type="number" value={oomQty} onChange={e => setOomQty(Number(e.target.value) || 1)} min={1}
                  style={{ width: '40px', padding: '0.35rem 0.3rem', border: `1px solid ${C.border}`, borderRadius: '5px', fontSize: '0.82rem', textAlign: 'center', boxSizing: 'border-box' }} />
                <button type="button" onClick={addOutOfMenuToCart}
                  style={{ ...tapMin, padding: '0.35rem 0.6rem', border: 'none', borderRadius: '6px', background: '#92400e', color: '#fff', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' }}>+ Add</button>
              </div>
            </div>
            <button type="button" onClick={createAndSendToKot}
              disabled={cartItems.length === 0}
              style={{ ...btnStyle, width: '100%', padding: '0.75rem', fontSize: '1rem', background: cartItems.length === 0 ? 'var(--theme-text-muted)' : C.primary, cursor: cartItems.length === 0 ? 'not-allowed' : 'pointer', borderRadius: '10px' }}>
              🍳 Place Order & Send to Kitchen
            </button>
          </div>
        </div>
      </div>
      )}

      {/* ════════════════════════════════════════════════════════
          BOTTOM: Recent Orders + KOT Tracker (full-width stacked, horizontal-scroll grids)
          ════════════════════════════════════════════════════════ */}
      {!step && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem', padding: isMobile ? '0 0.75rem' : '0 1.25rem' }}>

        {/* ── Recent Orders ── */}
        <div style={card}>
          {/* Order-type tabs */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
            {[
              { key: 'ALL', icon: '🗂️', label: 'All Orders', count: recentOrders.length, from: '#334155', to: '#1e293b', tint: '#f1f5f9', tintText: '#334155' },
              { key: 'ROOM_SERVICE', icon: '🛎️', label: 'Room Service', count: recentOrdersByType.ROOM_SERVICE.length, from: '#2563eb', to: '#1d4ed8', tint: '#eff6ff', tintText: '#1d4ed8' },
              { key: 'DINE_IN', icon: '🍴', label: 'Dine-In', count: recentOrdersByType.DINE_IN.length, from: '#7c3aed', to: '#6d28d9', tint: '#f5f3ff', tintText: '#6d28d9' },
              { key: 'TAKE_AWAY', icon: '🥡', label: 'Take Away', count: recentOrdersByType.TAKE_AWAY.length, from: '#d97706', to: '#b45309', tint: '#fffbeb', tintText: '#b45309' },
              { key: 'DELIVERY', icon: '🛵', label: 'Delivery', count: recentOrdersByType.DELIVERY.length, from: '#16a34a', to: '#15803d', tint: '#f0fdf4', tintText: '#15803d' },
            ].map((t) => {
              const active = recentOrdersTab === t.key;
              return (
                <button key={t.key} type="button" onClick={() => setRecentOrdersTab(t.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.45rem',
                    padding: '0.5rem 1rem', borderRadius: '10px', border: active ? 'none' : `1px solid ${C.border}`,
                    cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem',
                    background: active ? `linear-gradient(135deg, ${t.from}, ${t.to})` : '#fff',
                    color: active ? '#fff' : C.text,
                    boxShadow: active ? `0 2px 10px ${t.from}4d` : 'none',
                  }}>
                  <span>{t.icon}</span>
                  <span>{t.label}</span>
                  <span style={{
                    background: active ? 'rgba(255,255,255,0.25)' : t.tint,
                    color: active ? '#fff' : t.tintText,
                    borderRadius: 999, padding: '0.05rem 0.5rem', fontSize: '0.75rem', fontWeight: 800,
                  }}>{t.count}</span>
                </button>
              );
            })}
          </div>

          {/* Status filter row */}
          <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.85rem', flexWrap: 'wrap' }}>
            {[
              { key: 'ALL', icon: '📋', label: 'All', color: '#334155' },
              { key: 'PROCESSING', icon: '⚙️', label: 'Processing', color: '#b45309' },
              { key: 'BILLED', icon: '📄', label: 'Billed', color: '#1d4ed8' },
              { key: 'SETTLED', icon: '✅', label: 'Settled', color: '#15803d' },
              { key: 'PENDING', icon: '🕒', label: 'Pending', color: '#dc2626' },
              { key: 'CANCELLED', icon: '⛔', label: 'Cancelled', color: '#475569' },
            ].map((s) => {
              const active = recentOrdersStatusFilter === s.key;
              return (
                <button key={s.key} type="button" onClick={() => setRecentOrdersStatusFilter(s.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.35rem',
                    padding: '0.35rem 0.7rem', borderRadius: '8px',
                    border: active ? `1.5px solid ${s.color}` : `1px solid ${C.border}`,
                    cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem',
                    background: active ? `${s.color}14` : '#fff',
                    color: active ? s.color : C.muted,
                  }}>
                  <span>{s.icon}</span>
                  <span>{s.label}</span>
                  <span style={{ fontWeight: 800 }}>{statusCounts[s.key]}</span>
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '0.65rem', flexWrap: 'wrap', gap: '0.4rem' }}>
            <div>
              <div style={{ fontSize: '1rem', fontWeight: 800, color: C.text }}>
                {ORDER_TAB_META[recentOrdersTab].title}
              </div>
              <div style={{ fontSize: '0.78rem', color: C.muted }}>
                {ORDER_TAB_META[recentOrdersTab].subtitle}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="text" value={recentOrdersFilter} onChange={(e) => setRecentOrdersFilter(e.target.value)}
                placeholder="Filter by order #, table, guest…"
                style={{ ...inputStyle, width: 'auto', padding: '0.3rem 0.55rem', fontSize: '0.78rem', minWidth: '200px' }} />
              <select value={ordersPageSize} onChange={(e) => setOrdersPageSize(Number(e.target.value))}
                style={{ ...inputStyle, width: 'auto', padding: '0.3rem 0.5rem', fontSize: '0.78rem' }}>
                <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
              </select>
              <button type="button" onClick={() => setOrdersPage(p => Math.max(1, p - 1))} disabled={ordersPage <= 1}
                style={{ ...tapMin, padding: '0.3rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '6px', background: '#fff', cursor: ordersPage <= 1 ? 'not-allowed' : 'pointer', fontSize: '0.78rem', color: ordersPage <= 1 ? C.muted : C.text }}>‹</button>
              <span style={{ fontSize: '0.75rem', color: C.muted, whiteSpace: 'nowrap' }}>{ordersPage}/{totalOrderPages}</span>
              <button type="button" onClick={() => setOrdersPage(p => Math.min(totalOrderPages, p + 1))} disabled={ordersPage >= totalOrderPages}
                style={{ ...tapMin, padding: '0.3rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '6px', background: '#fff', cursor: ordersPage >= totalOrderPages ? 'not-allowed' : 'pointer', fontSize: '0.78rem', color: ordersPage >= totalOrderPages ? C.muted : C.text }}>›</button>
            </div>
          </div>
          {filteredRecentOrders.length === 0 ? (
            <div style={{ color: C.muted, fontSize: '0.85rem', padding: '1rem 0', textAlign: 'center' }}>
              {typeScopedOrders.length === 0 ? 'No orders found' : 'No orders match the filter'}
            </div>
          ) : (() => {
            const visible = pagedRecentOrders.slice(recentOrdersViewIndex, recentOrdersViewIndex + cardsPerView);
            const canPrev = recentOrdersViewIndex > 0;
            const canNext = recentOrdersViewIndex + cardsPerView < pagedRecentOrders.length;
            return (
            <div style={{ position: 'relative', overflow: 'hidden' }}>
              <button type="button" aria-label="Previous orders" disabled={!canPrev}
                onClick={() => setRecentOrdersViewIndex((i) => Math.max(0, i - cardsPerView))}
                style={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', zIndex: 2, width: 32, height: 32, borderRadius: '50%', border: `1px solid ${C.border}`, background: canPrev ? '#fff' : '#f1f5f9', boxShadow: canPrev ? '0 2px 6px rgba(0,0,0,0.12)' : 'none', cursor: canPrev ? 'pointer' : 'not-allowed', fontSize: '1.1rem', color: canPrev ? C.primary : C.muted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
              <button type="button" aria-label="Next orders" disabled={!canNext}
                onClick={() => setRecentOrdersViewIndex((i) => Math.min(Math.max(0, pagedRecentOrders.length - cardsPerView), i + cardsPerView))}
                style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', zIndex: 2, width: 32, height: 32, borderRadius: '50%', border: `1px solid ${C.border}`, background: canNext ? '#fff' : '#f1f5f9', boxShadow: canNext ? '0 2px 6px rgba(0,0,0,0.12)' : 'none', cursor: canNext ? 'pointer' : 'not-allowed', fontSize: '1.1rem', color: canNext ? C.primary : C.muted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cardsPerView}, minmax(0, 1fr))`, gap: '0.6rem', padding: '0 42px', overflow: 'hidden', boxSizing: 'border-box', width: '100%' }}>
              {visible.map((o) => {
                const items = Array.isArray(o.items) ? o.items : [];
                const accent = ORDER_STATUS_COLORS[orderStatusBucket(o)];
                const typeIcon = ORDER_TYPE_ICONS[orderTypeBucket(o)];
                return (
                <div key={o.id} style={{
                  border: o.bill_requested ? '2px solid #f59e0b' : `1px solid ${C.border}`,
                  borderLeft: o.bill_requested ? '2px solid #f59e0b' : `4px solid ${accent}`,
                  borderRadius: '10px', padding: '0.65rem 0.8rem',
                  background: o.bill_requested ? '#fffbeb' : '#fff',
                  position: 'relative',
                  minWidth: 0, overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
                }}>
                  {o.bill_requested && (
                    <div style={{
                      position: 'absolute', top: -10, right: 12,
                      background: '#f59e0b', color: '#fff',
                      padding: '2px 10px', borderRadius: 999,
                      fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.05em',
                      boxShadow: '0 2px 4px rgba(245,158,11,0.35)',
                    }}>🔔 CUSTOMER REQUESTED BILL</div>
                  )}
                  {o.is_self_order && !o.bill_requested && (
                    <div style={{
                      position: 'absolute', top: -10, right: 12,
                      background: '#7c3aed', color: '#fff',
                      padding: '2px 10px', borderRadius: 999,
                      fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.05em',
                    }}>📱 SELF-ORDER</div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.15rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '1rem' }}>{typeIcon}</span>
                    <span style={{ fontWeight: 700, color: C.primary, fontSize: '0.88rem' }}>#{o.order_no}</span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.3rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {statusBadge(o.status)}
                      {o.bill_status && o.bill_status !== o.status && statusBadge(o.bill_status)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: C.muted, marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                    {o.room_number && <span>🏨 Room {o.room_number}</span>}
                    {(o.table_no || tableNameById(o.table_id)) !== '-' && !o.room_number && <span>🪑 {o.table_no || tableNameById(o.table_id)}</span>}
                    {o.guest_name && <span>👤 {o.guest_name}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.75rem', color: C.muted, marginBottom: '0.45rem', flexWrap: 'wrap' }}>
                    <span>{items.length} Item{items.length === 1 ? '' : 's'}</span>
                    {o.opened_at && (() => {
                      // Show "N mins ago" with absolute time on hover.
                      const t = new Date(o.opened_at).getTime();
                      const mins = Math.round((Date.now() + serverTimeOffsetRef.current - t) / 60000);
                      const rel = mins < 1 ? 'just now'
                        : mins < 60 ? `${mins} min${mins === 1 ? '' : 's'} ago`
                        : (mins < 1440 ? `${Math.floor(mins/60)}h ${mins%60}m ago` : new Date(t).toLocaleString());
                      return (
                        <span title={`Placed ${new Date(t).toLocaleString()}`}>🕒 {rel}</span>
                      );
                    })()}
                    {o.closed_at && <span title="Order closed" style={{ color: C.success }}>Closed: {fmtTime(o.closed_at)}</span>}
                    {o.bill_no && <span style={{ color: C.success, fontWeight: 600 }}>Bill #{o.bill_no}</span>}
                  </div>
                  {items.length > 0 && (
                    <div style={{ borderTop: `1px dashed ${C.border}`, borderBottom: `1px dashed ${C.border}`, padding: '0.4rem 0', marginBottom: '0.4rem', display: 'grid', gap: '0.2rem' }}>
                      {items.map((it, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.78rem', color: C.text }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.menu_item_name} <span style={{ color: C.muted }}>×{Number(it.qty || 0)}</span></span>
                          <span style={{ flexShrink: 0, fontWeight: 600 }}>₹{Number(it.line_total ?? (it.unit_price * it.qty) ?? 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: '0.3rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 800, color: C.text, fontSize: '0.92rem' }}>₹{Number(o.order_total || 0).toFixed(2)}</span>
                    <span style={{ fontSize: '0.68rem', color: C.muted }}>(Incl. Tax)</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                    {canGenerateBill(o) && !tablesUsingConsolidatedBill.has(String(o.table_id || '')) && (
                      <button onClick={() => openGeneratePreview(o.id)} className="btn-primary" style={{ ...tapMin, padding: '0.3rem 0.7rem', border: 'none', borderRadius: '999px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>Generate Bill</button>
                    )}
                    {canGenerateBill(o) && tablesUsingConsolidatedBill.has(String(o.table_id || '')) && (
                      <span style={{ fontSize: '0.72rem', color: '#16a34a', fontWeight: 700, padding: '0.3rem 0' }}>Ready — use Generate Table Bill on the table</span>
                    )}
                    {o.bill_id && (
                      <>
                        <button onClick={() => viewBill(o.bill_id)} style={{ ...tapMin, padding: '0.3rem 0.7rem', border: `1px solid ${C.border}`, borderRadius: '999px', background: '#fff', color: C.accent, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>👁 View Bill</button>
                        <button onClick={() => printBill(o.bill_id)} style={{ ...tapMin, padding: '0.3rem 0.7rem', border: `1px solid ${C.border}`, borderRadius: '999px', background: '#fff', color: C.muted, cursor: 'pointer', fontSize: '0.75rem' }}>🖨 Print</button>
                      </>
                    )}
                    {/* Modify: for editable statuses (OPEN, PARTIAL_KOT) only — not READY (food already served) */}
                    {['OPEN', 'PARTIAL_KOT'].includes(o.status) && !o.bill_id && (
                      <button onClick={() => startEditOrder(o)} disabled={editLoading}
                        style={{ ...tapMin, padding: '0.3rem 0.7rem', border: `1px solid ${C.warn}`, borderRadius: '999px', background: '#fefce8', color: '#92400e', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>Modify</button>
                    )}
                    {/* View only: for non-editable in-progress statuses (KOT_SENT, PREPARING) */}
                    {['KOT_SENT', 'PREPARING'].includes(o.status) && !o.bill_id && (
                      <button onClick={() => startEditOrder(o)} disabled={editLoading}
                        style={{ ...tapMin, padding: '0.3rem 0.7rem', border: `1px solid ${C.border}`, borderRadius: '999px', background: '#dbeafe', color: '#1d4ed8', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>View</button>
                    )}
                    {o.bill_status !== 'SETTLED' && o.bill_status !== 'VOID' && o.status !== 'CANCELLED' && (
                      <button onClick={() => voidOrder(o)}
                        style={{ ...tapMin, padding: '0.3rem 0.7rem', border: `1px solid #fecaca`, borderRadius: '999px', background: '#fff', color: '#991b1b', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>🗑 Void</button>
                    )}
                    {o.bill_status === 'VOID' && (
                      <span title={o.void_reason || ''} style={{ fontSize: '0.72rem', color: '#991b1b', padding: '0.3rem 0', cursor: o.void_reason ? 'help' : 'default' }}>
                        Voided{o.voided_at ? ` at ${fmtTime(o.voided_at)}` : ''}
                      </span>
                    )}
                    {!canGenerateBill(o) && !o.bill_id && o.bill_status !== 'VOID' && ['PARTIAL_KOT', 'KOT_SENT', 'PREPARING'].includes(o.status) && (
                      <span style={{ fontSize: '0.72rem', color: C.muted, padding: '0.3rem 0' }}>Awaiting kitchen confirmation</span>
                    )}
                  </div>
                </div>
                );
              })}
              </div>
            </div>
            );
          })()}
        </div>

        {/* ── KOT Tracker ── */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.65rem', flexWrap: 'wrap', gap: '0.4rem' }}>
            <div style={{ ...secHead, marginBottom: 0, borderBottom: 'none' }}>Kitchen Order Tracker</div>
            <input type="text" value={kotsFilter} onChange={(e) => setKotsFilter(e.target.value)}
              placeholder="Filter by order #, KOT #, table…"
              style={{ ...inputStyle, width: 'auto', padding: '0.3rem 0.55rem', fontSize: '0.78rem', minWidth: '200px' }} />
          </div>
          {filteredKots.length === 0 ? (
            <div style={{ color: C.muted, fontSize: '0.85rem', padding: '1rem 0', textAlign: 'center' }}>{kots.length === 0 ? 'No active KOTs' : 'No KOTs match the filter'}</div>
          ) : (() => {
            const visibleKots = filteredKots.slice(kotsViewIndex, kotsViewIndex + cardsPerView);
            const canPrevKot = kotsViewIndex > 0;
            const canNextKot = kotsViewIndex + cardsPerView < filteredKots.length;
            return (
            <div style={{ position: 'relative', overflow: 'hidden' }}>
              <button type="button" aria-label="Previous KOTs" disabled={!canPrevKot}
                onClick={() => setKotsViewIndex((i) => Math.max(0, i - cardsPerView))}
                style={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', zIndex: 2, width: 32, height: 32, borderRadius: '50%', border: `1px solid ${C.border}`, background: canPrevKot ? '#fff' : '#f1f5f9', boxShadow: canPrevKot ? '0 2px 6px rgba(0,0,0,0.12)' : 'none', cursor: canPrevKot ? 'pointer' : 'not-allowed', fontSize: '1.1rem', color: canPrevKot ? C.primary : C.muted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
              <button type="button" aria-label="Next KOTs" disabled={!canNextKot}
                onClick={() => setKotsViewIndex((i) => Math.min(Math.max(0, filteredKots.length - cardsPerView), i + cardsPerView))}
                style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', zIndex: 2, width: 32, height: 32, borderRadius: '50%', border: `1px solid ${C.border}`, background: canNextKot ? '#fff' : '#f1f5f9', boxShadow: canNextKot ? '0 2px 6px rgba(0,0,0,0.12)' : 'none', cursor: canNextKot ? 'pointer' : 'not-allowed', fontSize: '1.1rem', color: canNextKot ? C.primary : C.muted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cardsPerView}, minmax(0, 1fr))`, gap: '0.6rem', padding: '0 44px', overflow: 'hidden', boxSizing: 'border-box', width: '100%' }}>
              {visibleKots.map((k) => {
                const stColor = { RAISED: C.warn, PREPARING: C.accent, READY: C.success, SERVED: 'var(--theme-text-muted)', CANCELLED: C.danger }[k.status] || C.muted;
                const kStatus = String(k.status || '').toUpperCase();
                const isCancelled = kStatus === 'CANCELLED' || kStatus === 'VOID';
                const stageIdx = kStatus === 'SERVED' ? 3 : kStatus === 'READY' ? 2 : kStatus === 'PREPARING' ? 1 : 0;
                const stages = [
                  { label: 'Received', time: k.raised_at },
                  { label: 'Preparing', time: k.prep_started_at },
                  { label: 'Ready', time: k.ready_at },
                  { label: 'Served', time: k.served_at },
                ];
                return (
                  <div key={k.id} style={{ border: `1.5px solid ${stColor}25`, borderLeft: `4px solid ${stColor}`, borderRadius: '8px', padding: '0.55rem 0.7rem', background: '#fff', minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, color: C.primary, fontSize: '0.88rem' }}>KOT #{k.kot_no}</span>
                      <span style={{ fontSize: '0.75rem', color: C.muted }}>Order #{k.order_no || k.order_id}</span>
                      {statusBadge(k.status)}
                      <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: C.muted, fontWeight: 600 }}>{stationNameById(k.station_id)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.72rem', color: C.muted, marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                      {k.order_type && orderTypePill(k.order_type)}
                      {k.table_no && <span>🪑 {k.table_no}</span>}
                      {k.raised_at && !k.served_at && !isCancelled && <span style={{ fontWeight: 600, color: C.warn, marginLeft: 'auto' }}>{(() => { const m = Math.floor((Date.now() + serverTimeOffsetRef.current - new Date(k.raised_at).getTime()) / 60000); return m < 1 ? '<1m ago' : m < 60 ? `${m}m ago` : `${Math.floor(m/60)}h ${m%60}m ago`; })()}</span>}
                    </div>
                    {isCancelled ? (
                      <div style={{ fontSize: '0.75rem', color: C.danger, fontWeight: 700, margin: '0.35rem 0 0.5rem' }}>KOT voided</div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'flex-start', margin: '0.4rem 0 0.55rem' }}>
                        {stages.map((stage, idx) => {
                          const done = idx <= stageIdx;
                          const isLast = idx === stages.length - 1;
                          return (
                            <React.Fragment key={stage.label}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                <div style={{ width: 11, height: 11, borderRadius: '50%', background: done ? stColor : '#e2e8f0', flexShrink: 0 }} />
                                <div style={{ fontSize: '0.64rem', fontWeight: 700, color: done ? C.text : C.muted, marginTop: 3, whiteSpace: 'nowrap' }}>{stage.label}</div>
                                <div style={{ fontSize: '0.58rem', color: C.muted, whiteSpace: 'nowrap' }}>{stage.time ? fmtTime(stage.time) : '—'}</div>
                              </div>
                              {!isLast && <div style={{ flex: 1, height: 2, background: idx < stageIdx ? stColor : '#e2e8f0', marginTop: 5, minWidth: 10 }} />}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                      {canStartKot(k) && (
                        <button onClick={() => kotStatusAction(k.id, 'start')} style={{ ...tapMin, padding: '0.25rem 0.65rem', border: 'none', borderRadius: '6px', background: C.accent, color: '#fff', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>▶ Start</button>
                      )}
                      {canReadyKot(k) && (
                        <button onClick={() => kotStatusAction(k.id, 'ready')} style={{ ...tapMin, padding: '0.25rem 0.65rem', border: 'none', borderRadius: '6px', background: C.warn, color: '#fff', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>✓ Ready</button>
                      )}
                      {canServeKot(k) && (
                        <button onClick={() => kotStatusAction(k.id, 'served')} style={{ ...tapMin, padding: '0.25rem 0.65rem', border: 'none', borderRadius: '6px', background: C.success, color: '#fff', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>🍽 Served</button>
                      )}
                      <button onClick={() => viewKot(k.id)} style={{ ...tapMin, padding: '0.25rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '6px', background: '#fff', color: C.muted, cursor: 'pointer', fontSize: '0.75rem' }}>View</button>
                      <button onClick={() => printKot(k.id)} style={{ ...tapMin, padding: '0.25rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '6px', background: '#fff', color: C.muted, cursor: 'pointer', fontSize: '0.75rem' }}>Print</button>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
            );
          })()}
        </div>
      </div>
      )}

      {/* ── Generate Bill Preview Modal (pre-bill, with discount input) ── */}
      {generatePreview && (() => {
        const items = generatePreview.items || [];
        const subTotal = items.reduce((s, it) => s + ((Number(it.qty || 0) * Number(it.unit_price || 0)) - Number(it.discount_amount || 0)), 0);
        const cgstBase = items.reduce((s, it) => s + Number(it.cgst_amount || 0), 0);
        const sgstBase = items.reduce((s, it) => s + Number(it.sgst_amount || 0), 0);
        const igstBase = items.reduce((s, it) => s + Number(it.igst_amount || 0), 0);
        const taxBase = items.reduce((s, it) => s + Number(it.tax_amount || 0), 0);
        const dv = Number(generatePreview.discountValue) || 0;
        let billDiscount = 0;
        if (dv > 0 && subTotal > 0) {
          if (generatePreview.discountType === 'PERCENT') billDiscount = subTotal * (Math.min(100, dv) / 100);
          else if (generatePreview.discountType === 'FIXED') billDiscount = Math.min(dv, subTotal);
        }
        const effectiveSub = Math.max(0, subTotal - billDiscount);
        const factor = subTotal > 0 ? effectiveSub / subTotal : 0;
        const cgstAdj = cgstBase * factor;
        const sgstAdj = sgstBase * factor;
        const igstAdj = igstBase * factor;
        const taxAdj = taxBase * factor;
        const grand = effectiveSub + taxAdj;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <div style={{ width: 'min(720px,96vw)', maxHeight: '90vh', overflow: 'auto', background: '#fff', borderRadius: '16px', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }}>
              <div style={{ background: C.primary, color: '#fff', padding: '1rem 1.25rem', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>Generate Bill</div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>Order #{generatePreview.order?.order_no || generatePreview.orderId} · Review items and apply discount</div>
                </div>
                <button onClick={() => setGeneratePreview(null)} style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: '8px', padding: '0.35rem 0.65rem', cursor: 'pointer', fontWeight: 700, fontSize: '1rem' }}>✕</button>
              </div>
              <div style={{ padding: '1rem 1.25rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--theme-bg-light)' }}>
                      <th style={{ padding: '0.5rem', textAlign: 'left', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>Item</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>Qty</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>Rate</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr><td colSpan={4} style={{ padding: '0.75rem', textAlign: 'center', color: C.muted }}>No items</td></tr>
                    ) : items.map((it, i) => (
                      <tr key={it.id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ padding: '0.5rem', fontWeight: 600, color: C.text }}>{it.menu_item_name || '-'}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', color: C.muted }}>{Number(it.qty || 0)}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', color: C.muted }}>{toMoney(it.unit_price)}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: C.text }}>{toMoney((Number(it.qty || 0) * Number(it.unit_price || 0)) - Number(it.discount_amount || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Discount input */}
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '0.75rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#9a3412', marginBottom: '0.5rem' }}>Apply Discount (optional)</div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    {[{ k: 'NONE', l: 'None' }, { k: 'PERCENT', l: '%' }, { k: 'FIXED', l: `${'\u20B9'} Fixed` }].map(opt => (
                      <button key={opt.k}
                        onClick={() => setGeneratePreview(prev => prev ? { ...prev, discountType: opt.k, discountValue: opt.k === 'NONE' ? '' : prev.discountValue } : prev)}
                        style={{
                          padding: '0.4rem 0.8rem', borderRadius: 8, border: '2px solid',
                          borderColor: generatePreview.discountType === opt.k ? '#ea580c' : '#d1d5db',
                          background: generatePreview.discountType === opt.k ? '#ea580c' : '#fff',
                          color: generatePreview.discountType === opt.k ? '#fff' : '#374151',
                          cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem',
                        }}>{opt.l}</button>
                    ))}
                    {generatePreview.discountType !== 'NONE' && (
                      <input type="number" min="0" step="0.01"
                        value={generatePreview.discountValue}
                        onChange={(e) => setGeneratePreview(prev => prev ? { ...prev, discountValue: e.target.value } : prev)}
                        placeholder={generatePreview.discountType === 'PERCENT' ? 'e.g. 10' : 'e.g. 50.00'}
                        style={{ flex: 1, minWidth: 120, padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box' }} />
                    )}
                    {billDiscount > 0 && (
                      <span style={{ fontSize: '0.82rem', color: '#9a3412', fontWeight: 600 }}>−₹{toMoney(billDiscount)}</span>
                    )}
                  </div>
                </div>

                {/* Totals */}
                <div style={{ background: 'var(--theme-bg-light)', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', color: C.muted, marginBottom: '0.2rem' }}>
                    <span>Sub Total</span><span>₹{toMoney(subTotal)}</span>
                  </div>
                  {billDiscount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', color: '#9a3412', marginBottom: '0.2rem' }}>
                      <span>Discount</span><span>−₹{toMoney(billDiscount)}</span>
                    </div>
                  )}
                  {cgstAdj > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', color: C.muted, marginBottom: '0.2rem' }}>
                      <span>CGST</span><span>₹{toMoney(cgstAdj)}</span>
                    </div>
                  )}
                  {sgstAdj > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', color: C.muted, marginBottom: '0.2rem' }}>
                      <span>SGST</span><span>₹{toMoney(sgstAdj)}</span>
                    </div>
                  )}
                  {igstAdj > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', color: C.muted, marginBottom: '0.2rem' }}>
                      <span>IGST</span><span>₹{toMoney(igstAdj)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, color: C.primary, fontSize: '1rem', borderTop: `1.5px solid ${C.border}`, paddingTop: '0.4rem', marginTop: '0.3rem' }}>
                    <span>Grand Total</span><span>₹{toMoney(grand)}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button onClick={() => setGeneratePreview(null)} disabled={generatePreview.generating}
                    style={{ ...btnStyle, background: C.muted }}>Cancel</button>
                  <button onClick={confirmGenerateBill} disabled={generatePreview.generating || items.length === 0}
                    style={{ ...btnStyle, background: generatePreview.generating ? '#94a3b8' : '#16a34a' }}>
                    {generatePreview.generating ? 'Generating...' : 'Confirm & Generate Bill'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Table Bill Preview Modal (consolidated, multi-order) ── */}
      {tableBillPreview && (() => {
        const items = tableBillPreview.items || [];
        const subTotal = items.reduce((s, it) => s + Number(it.line_total || 0) - Number(it.discount_amount || 0), 0);
        const cgstBase = items.reduce((s, it) => s + Number(it.cgst_amount || 0), 0);
        const sgstBase = items.reduce((s, it) => s + Number(it.sgst_amount || 0), 0);
        const igstBase = items.reduce((s, it) => s + Number(it.igst_amount || 0), 0);
        const taxBase = items.reduce((s, it) => s + Number(it.tax_amount || 0), 0);
        const dv = Number(tableBillPreview.discountValue) || 0;
        let billDiscount = 0;
        if (dv > 0 && subTotal > 0) {
          if (tableBillPreview.discountType === 'PERCENT') billDiscount = subTotal * (Math.min(100, dv) / 100);
          else if (tableBillPreview.discountType === 'FIXED') billDiscount = Math.min(dv, subTotal);
        }
        const effectiveSub = Math.max(0, subTotal - billDiscount);
        const factor = subTotal > 0 ? effectiveSub / subTotal : 0;
        const taxAdj = taxBase * factor;
        const grand = effectiveSub + taxAdj;
        const tableName = tableBillPreview.table?.table_name || tableBillPreview.table?.table_no || `Table ${tableBillPreview.table?.id}`;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <div style={{ width: 'min(720px,96vw)', maxHeight: '90vh', overflow: 'auto', background: '#fff', borderRadius: '16px', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }}>
              <div style={{ background: '#16a34a', color: '#fff', padding: '1rem 1.25rem', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>Generate Table Bill — {tableName}</div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>{tableBillPreview.orderIds.length} order{tableBillPreview.orderIds.length > 1 ? 's' : ''} combined · Review items and apply discount</div>
                </div>
                <button onClick={() => setTableBillPreview(null)} style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: '8px', padding: '0.35rem 0.65rem', cursor: 'pointer', fontWeight: 700, fontSize: '1rem' }}>✕</button>
              </div>
              <div style={{ padding: '1rem 1.25rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--theme-bg-light)' }}>
                      <th style={{ padding: '0.5rem', textAlign: 'left', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>Item</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>Qty</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>Rate</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr><td colSpan={4} style={{ padding: '0.75rem', textAlign: 'center', color: C.muted }}>No items</td></tr>
                    ) : items.map((it, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ padding: '0.5rem', fontWeight: 600, color: C.text }}>{it.menu_item_name || '-'}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', color: C.muted }}>{Number(it.qty || 0)}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', color: C.muted }}>{toMoney(it.unit_price)}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: C.text }}>{toMoney(it.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Discount input */}
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '0.75rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#9a3412', marginBottom: '0.5rem' }}>Apply Discount (optional)</div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    {[{ k: 'NONE', l: 'None' }, { k: 'PERCENT', l: '%' }, { k: 'FIXED', l: `${'₹'} Fixed` }].map(opt => (
                      <button key={opt.k}
                        onClick={() => setTableBillPreview(prev => prev ? { ...prev, discountType: opt.k, discountValue: opt.k === 'NONE' ? '' : prev.discountValue } : prev)}
                        style={{
                          padding: '0.4rem 0.8rem', borderRadius: 8, border: '2px solid',
                          borderColor: tableBillPreview.discountType === opt.k ? '#ea580c' : '#d1d5db',
                          background: tableBillPreview.discountType === opt.k ? '#ea580c' : '#fff',
                          color: tableBillPreview.discountType === opt.k ? '#fff' : '#374151',
                          cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem',
                        }}>{opt.l}</button>
                    ))}
                    {tableBillPreview.discountType !== 'NONE' && (
                      <input type="number" min="0" step="0.01"
                        value={tableBillPreview.discountValue}
                        onChange={(e) => setTableBillPreview(prev => prev ? { ...prev, discountValue: e.target.value } : prev)}
                        placeholder={tableBillPreview.discountType === 'PERCENT' ? 'e.g. 10' : 'e.g. 50.00'}
                        style={{ flex: 1, minWidth: 120, padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box' }} />
                    )}
                    {billDiscount > 0 && (
                      <span style={{ fontSize: '0.82rem', color: '#9a3412', fontWeight: 600 }}>−₹{toMoney(billDiscount)}</span>
                    )}
                  </div>
                </div>

                {/* Totals */}
                <div style={{ background: 'var(--theme-bg-light)', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', color: C.muted, marginBottom: '0.2rem' }}>
                    <span>Sub Total</span><span>₹{toMoney(subTotal)}</span>
                  </div>
                  {billDiscount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', color: '#9a3412', marginBottom: '0.2rem' }}>
                      <span>Discount</span><span>−₹{toMoney(billDiscount)}</span>
                    </div>
                  )}
                  {cgstBase * factor > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', color: C.muted, marginBottom: '0.2rem' }}>
                      <span>CGST</span><span>₹{toMoney(cgstBase * factor)}</span>
                    </div>
                  )}
                  {sgstBase * factor > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', color: C.muted, marginBottom: '0.2rem' }}>
                      <span>SGST</span><span>₹{toMoney(sgstBase * factor)}</span>
                    </div>
                  )}
                  {igstBase * factor > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', color: C.muted, marginBottom: '0.2rem' }}>
                      <span>IGST</span><span>₹{toMoney(igstBase * factor)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, color: '#16a34a', fontSize: '1rem', borderTop: `1.5px solid ${C.border}`, paddingTop: '0.4rem', marginTop: '0.3rem' }}>
                    <span>Grand Total</span><span>₹{toMoney(grand)}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button onClick={() => setTableBillPreview(null)} disabled={tableBillPreview.generating}
                    style={{ ...btnStyle, background: C.muted }}>Cancel</button>
                  <button onClick={confirmTableBill} disabled={tableBillPreview.generating || items.length === 0}
                    style={{ ...btnStyle, background: tableBillPreview.generating ? '#94a3b8' : '#16a34a' }}>
                    {tableBillPreview.generating ? 'Generating...' : 'Confirm & Generate Table Bill'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Bill Preview Modal ── */}
      {billPreview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ width: 'min(680px,96vw)', maxHeight: '90vh', overflow: 'auto', background: '#fff', borderRadius: '16px', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }}>
            <div style={{ background: C.primary, color: '#fff', padding: '1rem 1.25rem', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>Bill #{billPreview?.bill?.bill_no || '-'}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>Order #{billPreview?.bill?.order_no || '-'} · Table: {billPreview?.bill?.table_no || '-'} · {String(billPreview?.bill?.business_date || '').slice(0, 10)}</div>
              </div>
              <button onClick={() => setBillPreview(null)} style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: '8px', padding: '0.35rem 0.65rem', cursor: 'pointer', fontWeight: 700, fontSize: '1rem' }}>✕</button>
            </div>
            <div style={{ padding: '1rem 1.25rem' }}>
              {/* Same bill template as Print / View Bill everywhere in KOT & POS */}
              <div style={{ display: 'flex', justifyContent: 'center', background: 'var(--theme-bg-light)', borderRadius: '10px', padding: '0.5rem', marginBottom: '0.75rem' }}>
                <iframe
                  title="Bill preview"
                  srcDoc={buildBillPrintHtml(billPreview)}
                  style={{ width: '330px', maxWidth: '100%', height: '520px', border: `1px solid ${C.border}`, borderRadius: '6px', background: '#fff' }}
                />
              </div>
              {/* ── Settlement Panel ── */}
              {settlePanel && billPreview?.bill?.status === 'OPEN' && !settleSuccess && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '1rem', marginBottom: '0.75rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#166534', marginBottom: '0.6rem' }}>Settle Bill — Select Payment Mode</div>

                  {/* Payment mode buttons */}
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                    {[
                      { key: 'CASH', label: 'Cash', color: '#16a34a' },
                      { key: 'CARD', label: 'Card', color: '#2563eb' },
                      { key: 'UPI', label: 'UPI', color: '#7c3aed' },
                      { key: 'ROOM_CHARGE', label: 'Room Charge', color: '#ea580c' },
                      { key: 'COMPLIMENTARY', label: 'Complimentary', color: '#64748b' },
                      { key: 'BILL_TO_COMPANY', label: 'Bill to Company', color: '#1e3a8a' },
                    ].map(m => (
                      <button key={m.key} onClick={() => chooseSettleMode(m.key)}
                        style={{
                          padding: '0.4rem 0.8rem', borderRadius: 8, border: '2px solid',
                          borderColor: settleMode === m.key ? m.color : '#d1d5db',
                          background: settleMode === m.key ? m.color : '#fff',
                          color: settleMode === m.key ? '#fff' : '#374151',
                          cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem',
                        }}>{m.label}</button>
                    ))}
                  </div>

                  {/* Amount + Reference (for non-room, non-comp) */}
                  {settleMode !== 'ROOM_CHARGE' && settleMode !== 'COMPLIMENTARY' && settleMode !== 'BILL_TO_COMPANY' && (
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 120 }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Amount</label>
                        <input type="number" value={settleAmount} onChange={e => setSettleAmount(e.target.value)}
                          style={{ width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 120 }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Reference No</label>
                        <input value={settleRef} onChange={e => setSettleRef(e.target.value)} placeholder="Optional"
                          style={{ width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box' }} />
                      </div>
                    </div>
                  )}

                  {/* Room Charge fields */}
                  {settleMode === 'ROOM_CHARGE' && (
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 100 }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Room No *</label>
                        <input value={settleRoomNumber} onChange={e => setSettleRoomNumber(e.target.value)} placeholder="e.g. 101"
                          style={{ width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 120 }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Guest Name *</label>
                        <input value={settleGuestName} onChange={e => setSettleGuestName(e.target.value)} placeholder="Guest name"
                          style={{ width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 100 }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Mobile Last 4 *</label>
                        <input value={settleMobileLast4} onChange={e => setSettleMobileLast4(e.target.value)} placeholder="1234" maxLength={4}
                          style={{ width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box' }} />
                      </div>
                    </div>
                  )}

                  {/* Complimentary / NC fields */}
                  {settleMode === 'COMPLIMENTARY' && (
                    <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '0.75rem', marginBottom: '0.5rem' }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#92400e', marginBottom: '0.5rem' }}>NC Bill — Reason Required</div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: 2, minWidth: 160 }}>
                          <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#78350f' }}>Reason *</label>
                          <select value={ncReason} onChange={e => setNcReason(e.target.value)}
                            style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #fbbf24', borderRadius: 6, fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' }}>
                            <option value=''>— Select reason —</option>
                            <option value='Guest Complaint'>Guest Complaint</option>
                            <option value='Staff Meal'>Staff Meal</option>
                            <option value='Management Hosted'>Management Hosted</option>
                            <option value='PR / Promotion'>PR / Promotion</option>
                            <option value='Quality Issue'>Quality Issue</option>
                            <option value='Other'>Other</option>
                          </select>
                        </div>
                        <div style={{ flex: 2, minWidth: 140 }}>
                          <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#78350f' }}>Authorised By *</label>
                          <input value={ncAuthorisedBy} onChange={e => setNcAuthorisedBy(e.target.value)} placeholder="Manager / GM name"
                            style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #fbbf24', borderRadius: 6, fontSize: '0.85rem', boxSizing: 'border-box' }} />
                        </div>
                      </div>
                      {(() => {
                        // Outlet NC Bill Settings: the guest pays nothing; this %
                        // of the sale value is recorded as the bill's cost value.
                        const pct = Number(selectedOutlet?.nc_cost_percent || 0);
                        if (pct <= 0) return null;
                        const b = billPreview?.bill || {};
                        const sale = Math.max(0, Number(b.grand_total || 0) - Number(b.tax_total || 0));
                        const cost = Math.round(sale * pct) / 100;
                        return (
                          <div style={{ marginTop: '0.55rem', fontSize: '0.8rem', color: '#78350f', display: 'flex', flexWrap: 'wrap', gap: '0.3rem 1rem' }}>
                            <span>Sale value <b>₹{sale.toFixed(2)}</b></span>
                            <span>NC cost value ({pct}%) <b>₹{cost.toFixed(2)}</b> — recorded for the NC report</span>
                            <span>Collect from guest <b>₹0.00</b></span>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Bill to Company */}
                  {settleMode === 'BILL_TO_COMPANY' && (() => {
                    const lbl = { fontSize: '0.72rem', fontWeight: 600, color: '#1e3a8a' };
                    const inp = { width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #c7d2fe', borderRadius: 6, fontSize: '0.85rem', boxSizing: 'border-box', background: '#fff' };
                    const co = (btcCompanies || []).find((c) => String(c.corporate_id) === String(btcCorpId)) || null;
                    const grand = Number(billPreview?.bill?.grand_total || 0);
                    const limit = Number(co?.credit_limit || 0);
                    const owed = Number(co?.credit_used || 0);
                    const over = co && limit > 0 && owed + grand > limit ? owed + grand - limit : 0;
                    const money = (n) => `₹${Number(n || 0).toFixed(2)}`;
                    return (
                      <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '0.75rem', marginBottom: '0.5rem' }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e3a8a', marginBottom: '0.5rem' }}>Bill to Company — nothing is collected now; the company pays later</div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          <div style={{ flex: 3, minWidth: 200 }}>
                            <label style={lbl} htmlFor="btc-company">Company *</label>
                            <select id="btc-company" value={btcCorpId} onChange={(e) => { setBtcCorpId(e.target.value); setBtcMsg(''); }} style={inp}>
                              <option value="">{btcCompanies === null ? 'Loading companies…' : '— Select company —'}</option>
                              {(btcCompanies || []).map((c) => (
                                <option key={c.corporate_id} value={c.corporate_id}>{c.corporate_name}{c.corporate_code ? ` (${c.corporate_code})` : ''}</option>
                              ))}
                            </select>
                          </div>
                          <button type="button" onClick={() => { setBtcNewOpen((v) => !v); setBtcMsg(''); }}
                            style={{ padding: '0.4rem 0.7rem', borderRadius: 6, border: '1px solid #1e3a8a', background: '#fff', color: '#1e3a8a', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                            {btcNewOpen ? 'Close' : '+ New company'}
                          </button>
                        </div>

                        {btcNewOpen && (
                          <div style={{ background: '#fff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '0.6rem', marginTop: '0.5rem' }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e3a8a', marginBottom: '0.4rem' }}>New company (saved to Corporate Master)</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.4rem' }}>
                              {[
                                ['corporate_name', 'Company name *', 'e.g. HCL Technologies Ltd'],
                                ['gst_number', 'GSTIN', '15-digit GSTIN'],
                                ['contact_person', 'Contact person', ''],
                                ['mobile_number', 'Mobile', ''],
                                ['email', 'Email (for statements)', ''],
                                ['credit_limit', 'Credit limit ₹ (optional)', 'Blank = no limit'],
                              ].map(([k, l, ph]) => (
                                <div key={k}>
                                  <label style={lbl} htmlFor={`btc-new-${k}`}>{l}</label>
                                  <input id={`btc-new-${k}`} type={k === 'credit_limit' ? 'number' : 'text'} min={k === 'credit_limit' ? 0 : undefined}
                                    value={btcNew[k]} placeholder={ph} maxLength={k === 'gst_number' ? 15 : undefined}
                                    onChange={(e) => setBtcNew((f) => ({ ...f, [k]: k === 'gst_number' ? e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '') : e.target.value }))}
                                    style={{ ...inp, fontFamily: k === 'gst_number' ? 'monospace' : 'inherit' }} />
                                </div>
                              ))}
                            </div>
                            <div style={{ marginTop: '0.4rem' }}>
                              <label style={lbl} htmlFor="btc-new-address">Billing address</label>
                              <input id="btc-new-address" value={btcNew.billing_address} onChange={(e) => setBtcNew((f) => ({ ...f, billing_address: e.target.value }))} style={inp} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                              <button type="button" onClick={saveBtcCompany} disabled={btcBusy}
                                style={{ padding: '0.4rem 0.8rem', borderRadius: 6, border: 'none', background: '#1e3a8a', color: '#fff', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                                {btcBusy ? 'Saving…' : 'Save company'}
                              </button>
                            </div>
                          </div>
                        )}

                        {co && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.3rem 0.8rem', fontSize: '0.8rem', marginTop: '0.55rem', color: '#1f2937' }}>
                            <div><span style={{ color: '#64748b' }}>GSTIN:</span> <b>{co.gst_number || '—'}</b></div>
                            <div><span style={{ color: '#64748b' }}>Credit limit:</span> <b>{limit > 0 ? money(limit) : 'No limit'}</b></div>
                            <div><span style={{ color: '#64748b' }}>Outstanding:</span> <b>{money(owed)}</b></div>
                            {limit > 0 && <div><span style={{ color: '#64748b' }}>Available:</span> <b style={{ color: over ? '#b45309' : '#047857' }}>{money(Math.max(0, limit - owed))}</b></div>}
                            {co.billing_address && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#64748b' }}>Address:</span> {co.billing_address}</div>}
                          </div>
                        )}
                        {over > 0 && (
                          <div style={{ marginTop: '0.45rem', fontSize: '0.8rem', background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 6, padding: '0.35rem 0.55rem' }}>
                            This bill takes {co.corporate_name} {money(over)} over its credit limit of {money(limit)}. You can still settle it.
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.55rem' }}>
                          <div style={{ flex: 2, minWidth: 150 }}>
                            <label style={lbl} htmlFor="btc-guest">Guest / employee name *</label>
                            <input id="btc-guest" value={btcGuest} onChange={(e) => setBtcGuest(e.target.value)} maxLength={150} placeholder="Who signed the bill" style={inp} />
                          </div>
                          <div style={{ flex: 2, minWidth: 140 }}>
                            <label style={lbl} htmlFor="btc-ref">Employee ID / PO / Ref no</label>
                            <input id="btc-ref" value={btcRef} onChange={(e) => setBtcRef(e.target.value)} maxLength={120} placeholder="Optional" style={inp} />
                          </div>
                        </div>
                        <div style={{ marginTop: '0.4rem' }}>
                          <label style={lbl} htmlFor="btc-remarks">Remarks</label>
                          <input id="btc-remarks" value={btcRemarks} onChange={(e) => setBtcRemarks(e.target.value)} maxLength={255} placeholder="Optional" style={inp} />
                        </div>
                        {btcMsg && <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: '#b45309' }}>{btcMsg}</div>}
                      </div>
                    );
                  })()}

                  {/* GST Billing (optional) — Bill to Company uses the company's GST details */}
                  {settleMode !== 'BILL_TO_COMPANY' && (
                  <details style={{ marginBottom: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6, overflow: 'hidden' }}>
                    <summary style={{ padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, color: '#475569', background: '#f9fafb' }}>Customer GST Details (Optional)</summary>
                    <div style={{ padding: '0.5rem 0.6rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>GSTIN</label>
                        <input value={settleGstin} onChange={e => setSettleGstin(e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 15))} placeholder="15-digit GSTIN"
                          style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', fontFamily: 'monospace', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>Company Name</label>
                        <input value={settleCompanyName} onChange={e => setSettleCompanyName(e.target.value)} placeholder="Trade / Company name"
                          style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>Address</label>
                        <input value={settleCustomerAddr} onChange={e => setSettleCustomerAddr(e.target.value)} placeholder="Customer address"
                          style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', boxSizing: 'border-box' }} />
                      </div>
                    </div>
                    {settleGstin && settleGstin.length === 15 && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(settleGstin) && (
                      <div style={{ padding: '0.15rem 0.6rem 0.4rem', fontSize: '0.72rem', color: '#dc2626' }}>Invalid GSTIN format</div>
                    )}
                  </details>
                  )}

                  {settleError && <div style={{ color: '#991b1b', fontSize: '0.82rem', marginBottom: '0.4rem' }}>{settleError}</div>}

                  <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                    <button onClick={() => setSettlePanel(false)} disabled={settling}
                      style={{ ...btnStyle, background: '#94a3b8' }}>Cancel</button>
                    <button onClick={handleSettle} disabled={settling}
                      style={{ ...btnStyle, background: settling ? '#94a3b8' : '#16a34a' }}>
                      {settling ? 'Settling...' : 'Confirm Settlement'}
                    </button>
                  </div>
                </div>
              )}

              {/* Settle success */}
              {settleSuccess && (
                <div style={{ background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 10, padding: '1rem', marginBottom: '0.75rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>{'\u2705'}</div>
                  <div style={{ fontWeight: 700, color: '#166534', fontSize: '0.95rem' }}>{settleSuccess}</div>
                </div>
              )}

              {/* ── Action buttons ── */}
              {billPreview?.bill?.status === 'OPEN' && !settlePanel && !settleSuccess && (
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
                  {[
                    { key: 'update-kot',  label: 'Update KOT',      color: '#0891b2', action: () => { setBillPreview(null); closeBillAction(); } },
                    { key: 'print',       label: 'Print Bill',       color: '#16a34a', action: () => printBill(billPreview?.bill?.id) },
                    { key: 'split',       label: 'Split Bill',       color: '#0284c7', action: () => setBillAction(a => a === 'split' ? '' : 'split') },
                    { key: 'cancel',      label: 'Cancel Bill',      color: '#dc2626', action: () => setBillAction(a => a === 'cancel' ? '' : 'cancel') },
                    { key: 'discount',    label: 'Discount',         color: '#ea580c', action: () => setBillAction(a => a === 'discount' ? '' : 'discount') },
                    { key: 'transfer',    label: 'Table Transfer',   color: '#7c3aed', action: () => setBillAction(a => a === 'transfer' ? '' : 'transfer') },
                    { key: 'exemption',   label: 'NC Bill',          color: '#64748b', action: handleExemption },
                  ].map(b => (
                    <button key={b.key} onClick={b.action}
                      style={{ ...btnStyle, background: billAction === b.key ? b.color : '#fff', color: billAction === b.key ? '#fff' : b.color, border: `2px solid ${b.color}`, padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}>
                      {b.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Cancel Bill panel */}
              {billAction === 'cancel' && billPreview?.bill?.status === 'OPEN' && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.75rem', marginBottom: '0.6rem' }}>
                  <div style={{ fontWeight: 700, color: '#991b1b', fontSize: '0.85rem', marginBottom: '0.4rem' }}>Cancel Bill — Enter Reason</div>
                  <input value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Reason for cancellation (required)"
                    style={{ width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #fca5a5', borderRadius: 6, fontSize: '0.85rem', boxSizing: 'border-box', marginBottom: '0.5rem' }} />
                  <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                    <button onClick={closeBillAction} style={{ ...btnStyle, background: '#94a3b8', padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}>Back</button>
                    <button onClick={handleCancelBill} disabled={cancelBusy}
                      style={{ ...btnStyle, background: cancelBusy ? '#94a3b8' : '#dc2626', padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}>
                      {cancelBusy ? 'Cancelling...' : 'Confirm Cancel'}
                    </button>
                  </div>
                </div>
              )}

              {/* Discount panel */}
              {billAction === 'discount' && billPreview?.bill?.status === 'OPEN' && (
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '0.75rem', marginBottom: '0.6rem' }}>
                  <div style={{ fontWeight: 700, color: '#9a3412', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Apply Discount</div>
                  <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.5rem' }}>
                    {[{ k: 'PERCENT', l: '% Percent' }, { k: 'FIXED', l: '₹ Fixed' }].map(o => (
                      <button key={o.k} onClick={() => setDiscountType(o.k)}
                        style={{ padding: '0.35rem 0.75rem', borderRadius: 6, border: '2px solid #ea580c', background: discountType === o.k ? '#ea580c' : '#fff', color: discountType === o.k ? '#fff' : '#ea580c', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                  <input type="number" value={discountValue} onChange={e => setDiscountValue(e.target.value)}
                    placeholder={discountType === 'PERCENT' ? 'e.g. 10 (%)' : 'e.g. 50.00 (₹)'}
                    style={{ width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #fed7aa', borderRadius: 6, fontSize: '0.85rem', boxSizing: 'border-box', marginBottom: '0.4rem' }} />
                  <input value={discountReason} onChange={e => setDiscountReason(e.target.value)}
                    placeholder="Reason for discount (e.g. Loyalty, Complaint)"
                    style={{ width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #fed7aa', borderRadius: 6, fontSize: '0.85rem', boxSizing: 'border-box', marginBottom: '0.5rem' }} />
                  <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                    <button onClick={closeBillAction} style={{ ...btnStyle, background: '#94a3b8', padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}>Back</button>
                    <button onClick={handleApplyDiscount} disabled={discountBusy}
                      style={{ ...btnStyle, background: discountBusy ? '#94a3b8' : '#ea580c', padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}>
                      {discountBusy ? 'Applying...' : 'Apply Discount'}
                    </button>
                  </div>
                </div>
              )}

              {/* Table Transfer panel */}
              {billAction === 'transfer' && billPreview?.bill?.status === 'OPEN' && (
                <div style={{ background: '#faf5ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: '0.75rem', marginBottom: '0.6rem' }}>
                  <div style={{ fontWeight: 700, color: '#6d28d9', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Table Transfer</div>
                  <select value={transferTableId} onChange={e => setTransferTableId(e.target.value)}
                    style={{ width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #ddd6fe', borderRadius: 6, fontSize: '0.85rem', boxSizing: 'border-box', marginBottom: '0.5rem' }}>
                    <option value="">-- Select Table --</option>
                    {tables.filter(t => t.is_active && String(t.id) !== String(billPreview?.bill?.table_id)).map(t => (
                      <option key={t.id} value={t.id}>{t.table_no}</option>
                    ))}
                  </select>
                  <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                    <button onClick={closeBillAction} style={{ ...btnStyle, background: '#94a3b8', padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}>Back</button>
                    <button onClick={handleTableTransfer} disabled={transferBusy || !transferTableId}
                      style={{ ...btnStyle, background: transferBusy ? '#94a3b8' : '#7c3aed', padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}>
                      {transferBusy ? 'Transferring...' : 'Confirm Transfer'}
                    </button>
                  </div>
                </div>
              )}

              {/* Split Bill panel */}
              {billAction === 'split' && billPreview?.bill?.status === 'OPEN' && (() => {
                const grandTotal = Number(billPreview?.bill?.grand_total || 0);
                const splitCount = billPreview._splitCount || 2;
                const perHead = grandTotal > 0 ? (grandTotal / splitCount).toFixed(2) : '0.00';
                return (
                  <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '0.75rem', marginBottom: '0.6rem' }}>
                    <div style={{ fontWeight: 700, color: '#0369a1', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Split Bill</div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.85rem', color: '#374151' }}>Split among</span>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        {[2,3,4,5,6].map(n => (
                          <button key={n} onClick={() => setBillPreview(p => ({ ...p, _splitCount: n }))}
                            style={{ width: 36, height: 36, borderRadius: 8, border: '2px solid #0369a1', background: splitCount === n ? '#0369a1' : '#fff', color: splitCount === n ? '#fff' : '#0369a1', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>{n}</button>
                        ))}
                      </div>
                      <span style={{ fontSize: '0.85rem', color: '#374151' }}>guests</span>
                    </div>
                    <div style={{ background: '#fff', border: '1px solid #bae6fd', borderRadius: 6, padding: '0.6rem 0.75rem', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#475569' }}>
                        <span>Grand Total</span><span style={{ fontWeight: 700 }}>₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', color: '#0369a1', fontWeight: 800, marginTop: '0.25rem' }}>
                        <span>Each Guest Pays</span><span>₹{Number(perHead).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem' }}>
                      Collect ₹{perHead} from each of the {splitCount} guests separately. Each payment will be recorded individually.
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                      <button onClick={closeBillAction} style={{ ...btnStyle, background: '#94a3b8', padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}>Back</button>
                      <button onClick={() => { closeBillAction(); openSettlePanel(); }}
                        style={{ ...btnStyle, background: '#0369a1', padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}>
                        Proceed to Settle (×{splitCount})
                      </button>
                    </div>
                  </div>
                );
              })()}

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                {billPreview?.bill?.status === 'OPEN' && !settlePanel && !settleSuccess && (
                  <button onClick={openSettlePanel} style={{ ...btnStyle, background: '#2563eb' }}>Settle Bill</button>
                )}
                {(billPreview?.bill?.status !== 'OPEN' || settleSuccess) && (
                  <button onClick={() => printBill(billPreview?.bill?.id)} style={{ ...btnStyle, background: C.success }}>Print Bill</button>
                )}
                <button onClick={() => { setBillPreview(null); setSettlePanel(false); setSettleSuccess(''); closeBillAction(); }} style={{ ...btnStyle, background: C.muted }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── KOT Preview Modal ── */}
      {kotPreview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ width: 'min(560px,96vw)', maxHeight: '90vh', overflow: 'auto', background: '#fff', borderRadius: '16px', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }}>
            <div style={{ background: C.warn, color: '#fff', padding: '1rem 1.25rem', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>KOT #{kotPreview?.kot?.kot_no || '-'}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>Order #{kotPreview?.kot?.order_no || '-'} · {kotPreview?.kot?.station_name || '-'} · Table: {kotPreview?.kot?.table_no || '-'}</div>
              </div>
              <button onClick={() => setKotPreview(null)} style={{ border: 'none', background: 'rgba(255,255,255,0.2)', color: '#fff', borderRadius: '8px', padding: '0.35rem 0.65rem', cursor: 'pointer', fontWeight: 700, fontSize: '1rem' }}>✕</button>
            </div>
            <div style={{ padding: '1rem 1.25rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', marginBottom: '1rem' }}>
                <thead>
                  <tr style={{ background: 'var(--theme-bg-light)' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>Item</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>Qty</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>Instructions</th>
                  </tr>
                </thead>
                <tbody>
                  {(kotPreview.items || []).map((it, i) => (
                    <tr key={it.order_item_id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '0.5rem', fontWeight: 700, color: C.text }}>{it.menu_item_name || '-'}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 800, color: C.primary, fontSize: '1rem' }}>{Number(it.kot_qty || it.order_qty || 0)}</td>
                      <td style={{ padding: '0.5rem', color: C.muted, fontSize: '0.8rem' }}>{it.remarks || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={() => printKot(kotPreview?.kot?.id)} style={{ ...btnStyle, background: C.success }}>Print KOT</button>
                <button onClick={() => setKotPreview(null)} style={{ ...btnStyle, background: C.muted }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Combined Linked Orders Modal ── */}
      {linkedOrdersData && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ width: 'min(720px,96vw)', maxHeight: '90vh', overflow: 'auto', background: '#fff', borderRadius: '16px', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }}>
            <div style={{ background: '#1d4ed8', color: '#fff', padding: '1rem 1.25rem', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>🔗 Combined Bill</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>{(linkedOrdersData.linked_tables || []).join(' + ')}</div>
              </div>
              <button onClick={() => setLinkedOrdersData(null)} style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: '8px', padding: '0.35rem 0.65rem', cursor: 'pointer', fontWeight: 700, fontSize: '1rem' }}>✕</button>
            </div>
            <div style={{ padding: '1rem 1.25rem' }}>
              <div style={{ overflowX: 'auto', marginBottom: '0.75rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--theme-bg-light)' }}>
                      {['Table', 'Item', 'Qty', 'Rate', 'Amount'].map(h => (
                        <th key={h} style={{ padding: '0.5rem', textAlign: h === 'Qty' || h === 'Rate' || h === 'Amount' ? 'right' : 'left', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(linkedOrdersData.items || []).map((item, i) => (
                      <tr key={item.id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ padding: '0.5rem', color: C.accent, fontWeight: 600, fontSize: '0.8rem' }}>{item.source_table_name || '—'}</td>
                        <td style={{ padding: '0.5rem', fontWeight: 600, color: C.text }}>{item.menu_item_name || '—'}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', color: C.muted }}>{Number(item.qty_servings || 0)}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', color: C.muted }}>{Number(item.unit_price || 0).toFixed(2)}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 700, color: C.text }}>{Number(item.line_amount || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ background: 'var(--theme-bg-light)', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', color: C.muted, marginBottom: '0.2rem' }}>
                  <span>Subtotal</span><span>₹{Number(linkedOrdersData.combined?.subtotal || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', color: C.muted, marginBottom: '0.2rem' }}>
                  <span>Tax</span><span>₹{Number(linkedOrdersData.combined?.tax_total || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, color: C.primary, fontSize: '1rem', borderTop: `1.5px solid ${C.border}`, paddingTop: '0.4rem', marginTop: '0.3rem' }}>
                  <span>Total</span><span>₹{Number(linkedOrdersData.combined?.total_amount || 0).toFixed(2)}</span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setLinkedOrdersData(null)} style={{ ...btnStyle, background: C.muted }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Order Modal ── */}
      {editingOrder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ width: 'min(580px,96vw)', maxHeight: '90vh', overflow: 'auto', background: '#fff', borderRadius: '16px', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }}>
            <div style={{ background: C.primary, color: '#fff', padding: '1rem 1.25rem', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>Edit Order #{editingOrder.order_no}</div>
              <button onClick={() => setEditingOrder(null)} style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: '8px', padding: '0.35rem 0.65rem', cursor: 'pointer', fontWeight: 700, fontSize: '1rem' }}>✕</button>
            </div>
            <div style={{ padding: '1rem 1.25rem' }}>
              {editingOrder.items.length === 0 ? (
                <div style={{ color: C.muted, textAlign: 'center', padding: '1rem' }}>No items in this order</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                  {editingOrder.items.map(it => (
                    <div key={it.id} style={{ border: `1px solid ${C.border}`, borderRadius: '8px', padding: '0.6rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ flex: 1, fontWeight: 600, fontSize: '0.88rem' }}>{it.menu_item_name || it.item_name || it.menu_item_id}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          <button onClick={() => editItemQty(it.id, -1)} style={{ ...tapMin, width: '28px', height: '28px', padding: 0, border: 'none', borderRadius: '6px', background: 'var(--theme-border)', cursor: 'pointer', fontWeight: 700 }}>−</button>
                          <span style={{ minWidth: '24px', textAlign: 'center', fontWeight: 700, fontSize: '0.9rem' }}>{Number(it.qty)}</span>
                          <button onClick={() => editItemQty(it.id, 1)} style={{ ...tapMin, width: '28px', height: '28px', padding: 0, border: 'none', borderRadius: '6px', background: '#dcfce7', color: '#15803d', cursor: 'pointer', fontWeight: 700 }}>+</button>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: C.primary, minWidth: '65px', textAlign: 'right' }}>₹{(Number(it.qty) * Number(it.unit_price)).toFixed(2)}</div>
                        <button onClick={() => removeEditItem(it.id)} style={{ border: 'none', background: '#fef2f2', color: '#991b1b', cursor: 'pointer', borderRadius: '6px', padding: '0.25rem 0.5rem', fontSize: '0.75rem', fontWeight: 600 }}>Remove</button>
                      </div>
                      <input
                        value={it.remarks || ''}
                        onChange={(e) => editItemRemarks(it.id, e.target.value)}
                        placeholder="Special instructions..."
                        style={{ width: '100%', marginTop: '0.35rem', padding: '0.25rem 0.5rem', border: `1px solid ${C.border}`, borderRadius: '5px', fontSize: '0.75rem', boxSizing: 'border-box' }}
                      />
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={() => setEditingOrder(null)} style={{ ...tapMin, padding: '0.5rem 1rem', border: `1px solid ${C.border}`, borderRadius: '8px', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}>Cancel</button>
                <button onClick={saveEditOrder} disabled={editLoading}
                  style={{ ...tapMin, padding: '0.5rem 1.2rem', border: 'none', borderRadius: '8px', background: C.primary, color: '#fff', cursor: editLoading ? 'wait' : 'pointer', fontWeight: 700, fontSize: '0.88rem' }}>
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── PAX + Steward Modal (shown when selecting an available table) ── */}
      {showPaxModal && paxModalTable && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 380, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <h3 style={{ margin: '0 0 0.25rem', color: C.primary, fontSize: '1.1rem', fontWeight: 700 }}>
              Table {paxModalTable.table_no || paxModalTable.table_name || paxModalTable.id}
            </h3>
            <p style={{ color: C.muted, fontSize: '0.82rem', margin: '0 0 1.2rem' }}>Enter guest details to start the session</p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.3rem', color: C.text }}>
                Customer Name <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                type="text"
                value={customerNameInput}
                onChange={(e) => setCustomerNameInput(e.target.value)}
                placeholder="e.g. Mr. Sharma"
                style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.92rem', outline: 'none' }}
                autoFocus
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.3rem', color: C.text }}>
                Total PAX <span style={{ color: '#9ca3af', fontWeight: 400 }}>(guests)</span>
              </label>
              <input
                type="number"
                min="1"
                max="99"
                value={paxInput}
                onChange={(e) => setPaxInput(e.target.value)}
                placeholder="Enter number of guests"
                style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '1rem', outline: 'none' }}
              />
            </div>

            <div style={{ marginBottom: '1.2rem' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.3rem', color: C.text }}>
                Steward / Server Name
              </label>
              <input
                type="text"
                value={stewardInput}
                onChange={(e) => setStewardInput(e.target.value)}
                placeholder="e.g. Tilak, Ravi"
                style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.92rem', outline: 'none' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handlePaxModalCancel}
                style={{ ...tapMin, padding: '0.5rem 1.2rem', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: C.text, cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}
              >Cancel</button>
              <button
                onClick={handlePaxModalConfirm}
                style={{ ...tapMin, padding: '0.5rem 1.6rem', border: 'none', borderRadius: 8, background: C.primary, color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem' }}
              >Start Session</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default KOTModule;
